import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import GuestClaimModal from "@/components/GuestClaimModal";
import { useNavigate, useLocation } from "react-router-dom";
import UpgradeModal from "@/components/UpgradeModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import InvoiceEmailModal from "@/components/InvoiceEmailModal";
import InvoicePreviewModal from "@/components/InvoicePreviewModal";
import StornoKorrekturModal from "@/components/invoice/StornoKorrekturModal";
import DunningModal from "@/components/invoice/DunningModal";
// Feb 2026 — Compliance: Pflichthinweis-Vorschau (1:1 PDF-Text im Form sichtbar)
import LegalStatementPreview from "@/components/invoice/LegalStatementPreview";
// Feb 2026 — DACH-Smart-Inputs (Phase 6)
import { CurrencyInput } from "@/components/inputs/SmartInputs";
import { getVatIdPlaceholder } from "@/utils/i18nValidators";
import YearSelector from "@/components/YearSelector";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KPICard } from "@/components/ui/kpi-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Download, Edit, Trash2, FileText, Clock, Filter, TrendingUp, RotateCcw, HelpCircle, AlertCircle, Mail, FileX, Settings, Eye, Loader2, X, AlertTriangle, MessageSquare, QrCode, Lock, Unlock, FileWarning, FilePen, Bell, BadgeCheck } from "lucide-react";
import InvoiceDesignOverride from "@/components/invoices/InvoiceDesignOverride";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { trackEvent } from '@/lib/analytics';

// Country-specific VAT rates (outside component to avoid recreation)
// Feb 2026: 0%-Label NICHT mehr „Reverse Charge / Kleinunternehmer" — RC ist eine Rechnungs-Eigenschaft,
// kein Item-Steuersatz. Im Editor wählt man die Steuerart auf der Rechnungs-Card; 0% bedeutet hier
// nur „dieser Item ist steuerfrei" (kann z.B. eine Auslagen-Position auf einer Standard-Rechnung sein).
const VAT_RATES = {
  DE: [
    { value: 19, label: "19% (Regelsteuersatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 7, label: "7% (Ermäßigter Steuersatz)", description: "Lebensmittel, Bücher, ÖPNV" },
    { value: 0, label: "0% (steuerfrei)", description: "Nur einzeln steuerbefreite Positionen" }
  ],
  AT: [
    { value: 20, label: "20% (Normalsteuersatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 13, label: "13% (Ermäßigter Steuersatz)", description: "Lebensmittel, Bücher, kulturelle Dienstleistungen" },
    { value: 10, label: "10% (Stark ermäßigter Steuersatz)", description: "Bücher, Zeitungen, bestimmte Lebensmittel" },
    { value: 4.9, label: "4,9% (Grundnahrungsmittel, ab 1.7.2026)", description: "Ausgewählte Grundnahrungsmittel der Anlage 3 UStG (§ 10 Abs 1a) — nur Lieferungen, nicht Gastronomie" },
    { value: 0, label: "0% (steuerfrei)", description: "Nur einzeln steuerbefreite Positionen" }
  ],
  CH: [
    { value: 8.1, label: "8,1% (Normalsatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 3.8, label: "3,8% (Beherbergung)", description: "Hotellerie" },
    { value: 2.6, label: "2,6% (Reduzierter Satz)", description: "Lebensmittel, Bücher, Medikamente" },
    { value: 0, label: "0% (steuerfrei)", description: "Export oder Kleinunternehmer" }
  ]
};

const getVatRates = (country) => {
  return VAT_RATES[country] || VAT_RATES.DE;
};

const getDefaultVatRate = (country) => {
  const rates = getVatRates(country);
  const defaultRate = rates.find(r => r.isDefault);
  return defaultRate ? defaultRate.value : rates[0].value;
};

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = i18n.language === "de" ? de : enUS;
  
  const [invoices, setInvoices] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftGuardOpen, setDraftGuardOpen] = useState(false);
  const [guestClaimOpen, setGuestClaimOpen] = useState(false);
  const [autoCreateDialogOpen, setAutoCreateDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedProjectsForAuto, setSelectedProjectsForAuto] = useState([]);
  // Teilabrechnung (Sep 2026): nur Zeiteinträge bis einschließlich dieses Tages abrechnen ("" = alle)
  const [billUntil, setBillUntil] = useState("");
  const entryWithinCutoff = (e, cutoff = billUntil) => !cutoff || (e.start_time || "").slice(0, 10) <= cutoff;
  const applyBillUntil = (cutoff) => {
    setBillUntil(cutoff);
    setSelectedProjectsForAuto((prev) => prev.map((sp) => {
      const entries = timeEntries.filter(
        (e) => e.project_id === sp.id && (e.net_duration_minutes || e.duration_minutes) && !e.invoiced && entryWithinCutoff(e, cutoff)
      );
      const totalMinutes = entries.reduce((sum, e) => sum + (e.net_duration_minutes || e.duration_minutes), 0);
      return { ...sp, totalHours: totalMinutes / 60, entryCount: entries.length };
    }));
  };
  const [customerMismatchWarning, setCustomerMismatchWarning] = useState(null);
  const [pendingAutoProjectId, setPendingAutoProjectId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");
  const [taxHelpModalOpen, setTaxHelpModalOpen] = useState(false);
  const [showReverseChargeWarning, setShowReverseChargeWarning] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedInvoiceForEmail, setSelectedInvoiceForEmail] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [qrFallbackModalOpen, setQrFallbackModalOpen] = useState(false);
  const [qrFallbackData, setQrFallbackData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  // Feb 2026 — Storno + Korrektur (P0)
  const [stornoKorrekturOpen, setStornoKorrekturOpen] = useState(false);
  const [stornoKorrekturMode, setStornoKorrekturMode] = useState("storno"); // "storno" | "korrektur"
  const [stornoKorrekturInvoice, setStornoKorrekturInvoice] = useState(null);
  const [dunningOpen, setDunningOpen] = useState(false);
  const [dunningInvoice, setDunningInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);  // Initial-Listenladezustand für Skeleton-Feedback
  const [visibleCount, setVisibleCount] = useState(20);  // Pagination: Anzahl initial gerenderter Karten
  const [sbCommentInvoiceIds, setSbCommentInvoiceIds] = useState(new Set());
  // Feb 2026 — Sprint 1: Standardartikel-Picker für Rechnungs-Items
  const [standardArticles, setStandardArticles] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ 
    isOpen: false, 
    type: null, // 'delete' or 'reset'
    invoiceId: null,
    title: '',
    description: ''
  });
  const [formData, setFormData] = useState({
    design_override: null,
    customer_id: "",
    project_id: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    service_date: new Date().toISOString().split('T')[0],
    service_date_end: "",
    country: "DE",  // Will be updated by useEffect when user loads
    status: "created",
    notes: "",
    buyer_reference: "",  // BT-10: Leitweg-ID for XRechnung
    include_payment_qr: true,
    items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]  // Will be updated by useEffect
  });

  // Feb 2026 — Inline-Kunde direkt aus dem Rechnungs-Dialog
  const INLINE_CUSTOMER_DEFAULT = {
    name: "",
    email: "",
    phone: "",
    street: "",
    street_number: "",
    postal_code: "",
    city: "",
    country_code: "DE",
    vat_id: "",
  };
  const [inlineCustomer, setInlineCustomer] = useState(INLINE_CUSTOMER_DEFAULT);
  const [useInlineCustomer, setUseInlineCustomer] = useState(false);
  const [promoteDialog, setPromoteDialog] = useState({ isOpen: false, customerId: null, customerName: "" });

  useEffect(() => {
    // Initial-Load: parallel statt sequentiell für schnelleren Tab-Switch.
    // Rechnungen zuerst auflösen → Liste erscheint sofort, Rest lädt im Hintergrund.
    fetchInvoices().finally(() => setListLoading(false));
    Promise.all([
      fetchCustomers(),
      fetchProjects(),
      fetchTimeEntries(),
      fetchUserSettings(),
      fetchStandardArticles(),
    ]).catch(() => { /* einzelne Fehler werden in den Handlers geloggt */ });
  }, []);

  // Fetch SB comment indicators separately when user is available
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/advisor/clients/${user.id}/comments`).then(r => {
      const ids = new Set((r.data.comments || []).filter(c => c.entity_type === 'invoice' && c.entity_id).map(c => c.entity_id));
      setSbCommentInvoiceIds(ids);
    }).catch(() => {});
  }, [user?.id]);

  const openNewInvoiceDialog = () => {
    setEditingInvoice(null);
    setBillUntil("");
    const currentCountry = formData.country;
    setUseInlineCustomer(false);
    setInlineCustomer({ ...INLINE_CUSTOMER_DEFAULT, country_code: currentCountry || "DE" });
    setFormData({
      customer_id: "",
      project_id: "",
      invoice_date: format(new Date(), "yyyy-MM-dd"),
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
      service_date: "",
      service_date_end: "",
      country: currentCountry,
      status: "created",
      notes: userSettings?.invoice_design?.closing_text || "",
      buyer_reference: "",
      design_override: null,
      include_payment_qr: true,
      items: [
        {
          description: "",
          quantity: 1,
          unit_price: 0,
          tax_rate: getDefaultVatRate(currentCountry),
          total: 0,
        },
      ],
    });
    setDialogOpen(true);
  };

  // Entwurf-Schutz (2026): Rückfrage beim Schließen mit ungespeicherten Eingaben
  const isCreateInvoiceDirty = () => {
    if (editingInvoice) return false;
    if (useInlineCustomer && (inlineCustomer.name || "").trim()) return true;
    if (formData.customer_id) return true;
    if ((formData.items || []).some((it) => (it.description || "").trim() || Number(it.unit_price) > 0)) return true;
    if ((formData.notes || "").trim()) return true;
    return false;
  };

  const handleInvoiceDialogOpenChange = (next) => {
    if (!next && dialogOpen && isCreateInvoiceDirty()) {
      setDraftGuardOpen(true);   // nicht schließen — erst Rückfrage
      return;
    }
    setDialogOpen(next);
  };

  const discardInvoiceDraft = () => {
    setDraftGuardOpen(false);
    setDialogOpen(false);
    setEditingInvoice(null);
  };

  const saveInvoiceAsDraft = async () => {
    setDraftGuardOpen(false);
    await handleSubmit(null, { asDraft: true });
  };

  // Feb 2026 — Edit-Flow für Rechnungen im Status draft/created
  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setUseInlineCustomer(false);
    setInlineCustomer({ ...INLINE_CUSTOMER_DEFAULT, country_code: invoice.country || "DE" });
    setFormData({
      customer_id: invoice.customer_id || "",
      project_id: invoice.project_id || "",
      project_ids: invoice.project_ids || [],
      invoice_date: invoice.invoice_date ? format(new Date(invoice.invoice_date), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
      due_date: invoice.due_date ? format(new Date(invoice.due_date), "yyyy-MM-dd") : format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
      service_date: invoice.service_date ? format(new Date(invoice.service_date), "yyyy-MM-dd") : "",
      service_date_end: invoice.service_date_end ? format(new Date(invoice.service_date_end), "yyyy-MM-dd") : "",
      country: invoice.country || formData.country || "DE",
      status: invoice.status || "created",
      notes: invoice.notes || "",
      buyer_reference: invoice.buyer_reference || "",
      design_override: invoice.design_override || null,
      include_payment_qr: invoice.include_payment_qr !== false,
      items: Array.isArray(invoice.items) && invoice.items.length > 0
        ? invoice.items.map((it) => ({
            description: it.description || "",
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
            tax_rate: Number(it.tax_rate ?? getDefaultVatRate(invoice.country || "DE")),
            total: Number(it.total) || 0,
          }))
        : [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(invoice.country || "DE"), total: 0 }],
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1") {
      openNewInvoiceDialog();
      params.delete("create");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
    // Auto-create from time entries: ?auto_project=PROJECT_ID (from dashboard quick-invoice)
    const autoProjectId = params.get("auto_project");
    if (autoProjectId) {
      setPendingAutoProjectId(autoProjectId);
      setBillUntil("");
      setAutoCreateDialogOpen(true);
      params.delete("auto_project");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
    // Feb 2026 — Open invoice for editing (SB-request tap-to-fix from FL dashboard)
    const openInvoiceId = params.get("openInvoice");
    if (openInvoiceId) {
      // Lazy-load: invoice may not be in `invoices` state yet — trigger fetch + open after load
      api.get(`/invoices/${openInvoiceId}`)
        .then((r) => { if (r.data) handleEditInvoice(r.data); })
        .catch(() => { /* silent */ });
      params.delete("openInvoice");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
  }, [location.search, navigate]);

  // Process pending auto-project once data is loaded
  useEffect(() => {
    if (pendingAutoProjectId && projects.length > 0 && timeEntries.length > 0 && customers.length > 0) {
      addProjectToAutoSelection(pendingAutoProjectId);
      setPendingAutoProjectId(null);
    }
  }, [pendingAutoProjectId, projects, timeEntries, customers]);

  const [showLocked, setShowLocked] = useState(() => {
    try { return localStorage.getItem('ivoliq_show_locked') === '1'; } catch { return false; }
  });
  const toggleShowLocked = () => {
    setShowLocked(prev => {
      const next = !prev;
      try { localStorage.setItem('ivoliq_show_locked', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  useEffect(() => {
    filterInvoices();
    setVisibleCount(20);  // Bei Filterwechsel Pagination zurücksetzen → von oben anzeigen
  }, [invoices, selectedMonth, selectedYear, statusFilter, showLocked]);
  
  // Update formData when user loads (for correct country & VAT rate)
  useEffect(() => {
    if (user && user.country) {
      const defaultVatRate = getDefaultVatRate(user.country);
      setFormData(prev => {
        // Only update if country is different to avoid infinite loop
        if (prev.country !== user.country) {
          console.log('Updating formData country from', prev.country, 'to', user.country);
          return {
            ...prev,
            country: user.country,
            items: prev.items.map(item => ({
              ...item,
              tax_rate: defaultVatRate
            }))
          };
        }
        return prev;
      });
    }
  }, [user]);

  // Basis-Filter (Jahr/Monat/Festgeschrieben) — ohne Status, für Schnellfilter-Zählung wiederverwendbar
  const applyBaseFilters = (list) => {
    let f = [...list];
    if (!showLocked) f = f.filter(inv => !inv.is_locked);
    if (selectedYear) f = f.filter(inv => new Date(inv.invoice_date).getFullYear() === selectedYear);
    if (selectedMonth !== "all") f = f.filter(inv => (inv.invoice_date || "").substring(0, 7) === selectedMonth);
    return f;
  };

  // Status-Matching inkl. virtueller Filter (draft=created/draft, overdue=offen & fällig überschritten)
  const matchesStatus = (inv, sf) => {
    if (sf === "all") return true;
    if (sf === "draft") return inv.status === "created" || inv.status === "draft";
    if (sf === "overdue") {
      const isOpen = (inv.status === "sent" || inv.status === "created" || inv.status === "draft") && !inv.paid_at;
      return isOpen && !!inv.due_date && new Date(inv.due_date) < new Date();
    }
    return inv.status === sf;
  };

  const filterInvoices = () => {
    const filtered = applyBaseFilters(invoices).filter(inv => matchesStatus(inv, statusFilter));
    setFilteredInvoices(filtered);
  };

  // Zählungen für die Schnellfilter-Chips (Basis = Jahr/Monat/Festgeschrieben, ohne Status)
  const quickFilters = [
    { key: "all", label: "Alle" },
    { key: "draft", label: "Entwürfe" },
    { key: "sent", label: "Offen" },
    { key: "overdue", label: "Überfällig" },
    { key: "paid", label: "Bezahlt" },
  ];
  const getQuickFilterCounts = () => {
    const base = applyBaseFilters(invoices);
    const counts = {};
    quickFilters.forEach(qf => { counts[qf.key] = base.filter(inv => matchesStatus(inv, qf.key)).length; });
    return counts;
  };

  const getMonthOptions = () => {
    const options = [{ value: "all", label: t('common.allMonths', 'Alle Monate') }];
    // Generate months for the selected year
    for (let i = 0; i < 12; i++) {
      const date = new Date(selectedYear, i, 1);
      options.push({
        value: format(date, "yyyy-MM"),
        label: format(date, "MMMM yyyy", { locale })
      });
    }
    return options;
  };

  const calculateStats = () => {
    const total = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);
    const paid = filteredInvoices.filter(inv => inv.status === "paid").reduce((sum, inv) => sum + inv.total, 0);
    const created = filteredInvoices.filter(inv => inv.status === "created" || inv.status === "draft").reduce((sum, inv) => sum + inv.total, 0);
    const sent = filteredInvoices.filter(inv => inv.status === "sent").reduce((sum, inv) => sum + inv.total, 0);
    const open = sent; // sent but not paid = open
    
    return { total, paid, created, open, count: filteredInvoices.length };
  };


  const fetchInvoices = async () => {
    try {
      console.log('🔵 Fetching invoices...');
      const response = await api.get('/invoices');
      console.log('🔵 Received invoices:', response.data.length);
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInvoices(sorted);
      console.log('🔵 Invoices state updated');
    } catch (error) {
      console.error("❌ Error fetching invoices:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get('/time-entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error("Error fetching time entries:", error);
    }
  };

  // Feb 2026 — Sprint 1: Standardartikel laden für Picker im Item-Form
  const fetchStandardArticles = async () => {
    try {
      const response = await api.get('/standard-articles');
      setStandardArticles(response.data.articles || []);
    } catch (error) {
      // Optional — wenn API fehlt einfach leeres Array
      setStandardArticles([]);
    }
  };

  // Übernimmt einen Standardartikel in die Item-Zeile (alle Felder bleiben editierbar)
  const applyStandardArticle = (index, articleId) => {
    if (!articleId) return;
    const a = standardArticles.find(x => x.id === articleId);
    if (!a) return;
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      description: a.description ? `${a.name} — ${a.description}` : a.name,
      quantity: a.default_quantity ?? 1,
      unit_price: a.unit_price ?? 0,
      tax_rate: a.vat_rate ?? updatedItems[index].tax_rate ?? 19,
      total: (a.default_quantity ?? 1) * (a.unit_price ?? 0),
    };
    setFormData({ ...formData, items: updatedItems });
  };

  const fetchCountries = async () => {
    // Not needed anymore, only DE
    return { DE: { name: "Deutschland", currency: "EUR" } };
  };

  const fetchUserSettings = async () => {
    try {
      const response = await api.get('/settings');
      setUserSettings(response.data);
    } catch (error) {
      console.error("Error fetching user settings:", error);
      // Settings are optional, so don't show error to user
    }
  };


  // Helper: add a project to the multi-project selection
  const addProjectToAutoSelection = (projectId) => {
    if (!projectId || selectedProjectsForAuto.find(p => p.id === projectId)) return;
    
    // Get uninvoiced time entries for this project
    const entries = timeEntries.filter(
      e => e.project_id === projectId && (e.net_duration_minutes || e.duration_minutes) && !e.invoiced && entryWithinCutoff(e)
    );
    if (entries.length === 0) return;

    const totalMinutes = entries.reduce((sum, e) => sum + (e.net_duration_minutes || e.duration_minutes), 0);
    const totalHours = totalMinutes / 60;

    // Try to find project in projects list, fallback to time entry data
    const project = projects.find(p => p.id === projectId);
    const projectName = project?.name || entries[0]?.project_name || "Unbekannt";
    const customerId = project?.customer_id || "";
    const hourlyRate = project?.hourly_rate || 0;
    const customer = customerId ? customers.find(c => c.id === customerId) : null;
    const customerName = customer?.name || "Unbekannt";

    // Check for customer mismatch
    if (selectedProjectsForAuto.length > 0 && customerId) {
      const firstCustomerId = selectedProjectsForAuto[0].customer_id;
      if (firstCustomerId && customerId !== firstCustomerId) {
        const firstCustomer = customers.find(c => c.id === firstCustomerId);
        setCustomerMismatchWarning({
          projectToAdd: { id: projectId, name: projectName, customer_id: customerId, customerName, hourly_rate: hourlyRate },
          existingCustomerName: firstCustomer?.name || "Unbekannt",
          newCustomerName: customerName,
        });
        return;
      }
    }

    setSelectedProjectsForAuto(prev => [...prev, {
      id: projectId,
      name: projectName,
      customer_id: customerId,
      customerName,
      hourly_rate: hourlyRate,
      totalHours,
      entryCount: entries.length,
    }]);
  };

  // Confirm adding a project despite customer mismatch
  const confirmCustomerMismatch = () => {
    if (customerMismatchWarning?.projectToAdd) {
      const project = customerMismatchWarning.projectToAdd;
      const entries = timeEntries.filter(
        e => e.project_id === project.id && (e.net_duration_minutes || e.duration_minutes) && !e.invoiced && entryWithinCutoff(e)
      );
      const totalMinutes = entries.reduce((sum, e) => sum + (e.net_duration_minutes || e.duration_minutes), 0);
      const totalHours = totalMinutes / 60;

      setSelectedProjectsForAuto(prev => [...prev, {
        id: project.id,
        name: project.name,
        customer_id: project.customer_id || "",
        customerName: project.customerName || "Unbekannt",
        hourly_rate: project.hourly_rate || 0,
        totalHours,
        entryCount: entries.length,
      }]);
    }
    setCustomerMismatchWarning(null);
  };

  const removeProjectFromAutoSelection = (projectId) => {
    setSelectedProjectsForAuto(prev => prev.filter(p => p.id !== projectId));
  };

  const createInvoiceFromTimeEntries = async () => {
    // Check if user has access to automatic invoice creation (Basic, Pro, or Lifetime)
    const hasAutomaticInvoices = user && (
      user.plan === 'basic' ||
      user.plan === 'basic_monthly' || 
      user.plan === 'basic_yearly' ||
      user.plan === 'pro' || 
      user.plan === 'pro_monthly' || 
      user.plan === 'pro_yearly' ||
      user.plan === 'lifetime'
    );

    if (!hasAutomaticInvoices) {
      toast.error('Automatische Rechnungserstellung ist erst ab dem Basic Plan verfügbar. Bitte upgraden Sie Ihren Plan.');
      setUpgradeModalOpen(true);
      return;
    }

    if (selectedProjectsForAuto.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Projekt aus');
      return;
    }
    if (selectedProjectsForAuto.reduce((sum, sp) => sum + sp.totalHours, 0) <= 0) {
      toast.error(`Im gewählten Zeitraum (bis ${billUntil}) gibt es keine offenen Stunden — Datum anpassen oder „Alle offenen" wählen.`);
      return;
    }

    const currentCountry = formData.country;

    // Create one invoice item per selected project
    const invoiceItems = selectedProjectsForAuto.map(sp => ({
      description: `Arbeitszeit für ${sp.name}`,
      quantity: parseFloat(sp.totalHours.toFixed(2)),
      unit_price: sp.hourly_rate,
      tax_rate: getDefaultVatRate(currentCountry),
      total: parseFloat((sp.totalHours * sp.hourly_rate).toFixed(2))
    }));

    const totalEntryCount = selectedProjectsForAuto.reduce((sum, sp) => sum + sp.entryCount, 0);
    const totalHours = selectedProjectsForAuto.reduce((sum, sp) => sum + sp.totalHours, 0);

    // Use customer from first project
    const primaryCustomerId = selectedProjectsForAuto[0].customer_id;
    const projectIds = selectedProjectsForAuto.map(sp => sp.id);
    const projectNames = selectedProjectsForAuto.map(sp => sp.name).join(", ");

    // Leistungszeitraum automatisch aus den offenen Zeitbuchungen:
    // Start = erster gebuchter Tag, Ende = letzter gebuchter Tag (One-Click-Erstellung)
    const relevantEntries = timeEntries.filter(
      e => projectIds.includes(e.project_id) && (e.net_duration_minutes || e.duration_minutes) && !e.invoiced && entryWithinCutoff(e)
    );
    const entryTimes = relevantEntries.map(e => new Date(e.start_time).getTime()).filter(t => !isNaN(t));
    const serviceStart = entryTimes.length ? format(new Date(Math.min(...entryTimes)), "yyyy-MM-dd") : "";
    const serviceEndRaw = entryTimes.length ? format(new Date(Math.max(...entryTimes)), "yyyy-MM-dd") : "";
    const serviceEnd = serviceEndRaw !== serviceStart ? serviceEndRaw : "";

    // Set form data
    setFormData({
      customer_id: primaryCustomerId,
      project_id: projectIds[0],
      project_ids: projectIds,
      invoice_date: format(new Date(), "yyyy-MM-dd"),
      due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
      service_date: serviceStart,
      service_date_end: serviceEnd,
      country: currentCountry,
      status: "created",
      notes: selectedProjectsForAuto.length > 1
        ? `Abrechnung für ${totalEntryCount} Zeiteinträge aus ${selectedProjectsForAuto.length} Projekten (${projectNames})`
        : `Abrechnung für ${totalEntryCount} Zeiteinträge`,
      buyer_reference: "",
      include_payment_qr: true,
      items: invoiceItems
    });

    setAutoCreateDialogOpen(false);
    setDialogOpen(true);
    setSelectedProjectsForAuto([]);
    toast.success(`Rechnung aus ${totalHours.toFixed(2)} Stunden erstellt${selectedProjectsForAuto.length > 1 ? ` (${selectedProjectsForAuto.length} Projekte)` : ''}`);
    trackInvoiceCreated();
  };

  // Funnel-Tracking (Paket A, Juni 2026): invoice_created + first_invoice
  const trackInvoiceCreated = () => {
    trackEvent('invoice_created');
    if (invoices.length === 0) trackEvent('first_invoice');
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Recalculate total for this item
    if (field === "quantity" || field === "unit_price") {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    const defaultVatRate = getDefaultVatRate(formData.country);
    // Feb 2026 — bei KU oder Cross-Border (RC/IGL/Export) ist tax_rate per Gesetz 0.
    // Wir referenzieren `user?.is_kleinunternehmer` direkt, nicht die `isKU`-Variable —
    // weil diese Funktion auch von Bereichen aufgerufen werden kann, wo `isKU` evtl. noch
    // nicht in Scope ist (siehe TDZ-Bug im Sync-useEffect oben).
    const isKuLocal = user?.is_kleinunternehmer === true;
    const sellerCountry = (user?.country || 'DE').toUpperCase();
    const selectedCustomer = useInlineCustomer
      ? inlineCustomer
      : customers.find(c => c.id === formData.customer_id);
    const customerCountry = (selectedCustomer?.country_code || '').toUpperCase();
    const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
    const scenario = formData.tax_scenario || (isCrossBorder ? detectTaxScenario() : 'standard');
    const forceZero = isKuLocal || (isCrossBorder && scenario !== 'standard');
    const tax_rate = forceZero ? 0 : defaultVatRate;
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, tax_rate, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Feb 2026 — Outbound Steuerart-Auto-Detection (EU + Drittland-RC)
  // WICHTIG: erkennt Cross-Border bereits bei Kundenauswahl — UNABHÄNGIG davon, ob die
  // Items schon auf 0% stehen. So erscheint die RC-Card sofort bei AT-Kunden (im DE-Account)
  // und der User wird zur korrekten Steuerart geführt, bevor er den 0%-Satz manuell setzt.
  const detectTaxScenario = () => {
    if (!formData.customer_id && !useInlineCustomer) return "standard";
    const customer = useInlineCustomer
      ? { ...inlineCustomer, country_code: inlineCustomer.country_code || user?.country }
      : customers.find(c => c.id === formData.customer_id);
    if (!customer) return "standard";
    const sellerCountry = (user?.country || 'DE').toUpperCase();
    const customerCountry = (customer.country_code || '').toUpperCase();
    if (!customerCountry || customerCountry === sellerCountry) return "standard";
    const EU = ['DE','AT','BE','BG','CY','CZ','DK','EE','ES','FI','FR','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK'];
    const isEU = EU.includes(customerCountry);
    const descs = (formData.items || []).map(it => (it.description || '').toLowerCase()).join(' ');
    const serviceKw = ['beratung','consulting','entwicklung','service','leistung','stunden','honorar','freelance','support','training','coaching','lizenz','subscription','software','saas'];
    const looksService = serviceKw.some(k => descs.includes(k));
    if (isEU && customer.vat_id) {
      return looksService ? "reverse_charge" : "intra_community";
    }
    if (!isEU) {
      return looksService ? "reverse_charge" : "export";  // Drittland: Service → RC, Ware → Export
    }
    return "standard";
  };

  // Check if Reverse Charge might apply
  const checkReverseCharge = () => detectTaxScenario() === "reverse_charge";

  // Update reverse charge warning when customer changes
  useEffect(() => {
    setShowReverseChargeWarning(checkReverseCharge());
  }, [formData.customer_id, formData.items, useInlineCustomer, inlineCustomer, customers]);

  // Feb 2026 — Cross-Border-Auto-Sync: bei RC/IGL/Export müssen alle items.tax_rate=0 sein.
  // Dieser Effect feuert bei Kunden-Wechsel und stellt sicher, dass die Summen-Anzeige
  // (Zwischensumme + MwSt-Gesamt) konsistent mit dem ausgewählten Szenario ist.
  // Wichtig: nur normalisieren, wenn tatsächlich noch != 0 — sonst Endlos-Loop.
  // Hinweis: `user?.is_kleinunternehmer` wird direkt referenziert, weil die Variable `isKU`
  // weiter unten in der Komponente deklariert wird (TDZ — Temporal Dead Zone Problem).
  useEffect(() => {
    if (user?.is_kleinunternehmer === true) return;
    const sellerCountry = (user?.country || 'DE').toUpperCase();
    const selectedCustomer = useInlineCustomer
      ? inlineCustomer
      : customers.find(c => c.id === formData.customer_id);
    const customerCountry = (selectedCustomer?.country_code || '').toUpperCase();
    const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
    if (!isCrossBorder) return;
    const scenario = formData.tax_scenario || detectTaxScenario();
    if (scenario === 'standard') return;
    const hasNonZero = (formData.items || []).some(it => Number(it.tax_rate || 0) !== 0);
    if (!hasNonZero) return;
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).map(it => ({
        ...it,
        tax_rate: 0,
        total: Number(it.quantity || 0) * Number(it.unit_price || 0)
      }))
    }));
    // Intentionale Subset-Deps: customers/detectTaxScenario hängen vom Render-Tree ab,
    // diese als Deps würden Endlos-Re-Runs auslösen. Watch-Kette ist auf den
    // Customer-Selektor-State + Steuerart fokussiert. `customers.length` als Trigger
    // damit nach dem asynchronen customers-Fetch der Auto-Sync auch retroaktiv läuft.
    // eslint-disable-next-line
  }, [formData.customer_id, formData.tax_scenario, useInlineCustomer, inlineCustomer?.country_code, user?.is_kleinunternehmer, user?.country, customers.length]);

  const handleSubmit = async (e, opts = {}) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const asDraft = opts.asDraft === true;

    // Validation: Service date is required (Entwurf: notfalls Rechnungsdatum als Fallback)
    const serviceDate = formData.service_date || (asDraft ? formData.invoice_date : "");
    if (!serviceDate) {
      toast.error(t('invoices.serviceDateRequired', 'Service date is required'));
      return;
    }

    // Feb 2026 — Inline-Kunde-Validation
    if (useInlineCustomer && !inlineCustomer.name.trim()) {
      toast.error("Bitte gib einen Kundennamen ein.");
      return;
    }
    if (!useInlineCustomer && !formData.customer_id && !editingInvoice && !asDraft) {
      toast.error("Bitte einen Kunden auswählen oder neuen Kunden eingeben.");
      return;
    }

    // Juni 2026 — Sanfter Hinweis: AT-Rechnung > 10.000 € brutto an Unternehmer braucht die
    // Kunden-UID (§ 11 Abs. 1 UStG). Nicht blockierend — ohne UID wird auf der Rechnung der
    // zulässige Vermerk „Keine UID angegeben" ausgewiesen.
    if (!asDraft) {
      const invCountry = (formData.country || 'DE').toUpperCase();
      const selCust = useInlineCustomer ? inlineCustomer : customers.find(c => c.id === formData.customer_id);
      const hasCustomerVat = !!(selCust?.vat_id && String(selCust.vat_id).trim());
      const grossTotal = (formData.items || []).reduce((sum, it) => {
        const net = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        return sum + net + net * ((Number(it.tax_rate) || 0) / 100);
      }, 0);
      if (invCountry === 'AT' && grossTotal > 10000 && !hasCustomerVat) {
        toast.warning(
          'AT-Rechnung über 10.000 € brutto: Die UID des Kunden ist hier Pflicht (§ 11 UStG). Ohne UID erscheint auf der Rechnung „Keine UID angegeben".',
          { duration: 7000 }
        );
      }
    }
    
    try {
      const data = {
        ...formData,
        status: asDraft ? "created" : formData.status,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        service_date: new Date(serviceDate).toISOString(),
        service_date_end: formData.service_date_end ? new Date(formData.service_date_end).toISOString() : null,
      };

      // Entwurf ohne gewählten Kunden: Platzhalter-Inline-Kunde, damit der Entwurf
      // gespeichert werden kann (Backend verlangt customer_id ODER inline_customer).
      if (asDraft && !editingInvoice && !useInlineCustomer && !formData.customer_id) {
        data.customer_id = undefined;
        data.inline_customer = { name: "Entwurf – noch kein Kunde", country_code: formData.country || "DE" };
      }

      // Bei Inline-Kunde: customer_id leeren und inline_customer mitschicken
      if (useInlineCustomer && !editingInvoice) {
        data.customer_id = undefined;
        data.inline_customer = { ...inlineCustomer };
      }

      if (editingInvoice) {
        console.log('🟡 Updating invoice:', editingInvoice.id);
        await api.put(`/invoices/${editingInvoice.id}`, data);
        toast.success(t("common.save"));
        
        // Mark time entries as invoiced if this was created from time tracking
        if (formData.project_ids?.length > 0) {
          try {
            const pids = formData.project_ids.join(",");
            console.log(`🔗 Linking time entries for projects [${pids}] to invoice ${editingInvoice.id}`);
            await api.post(`/time-entries/mark-invoiced?project_ids=${pids}&invoice_id=${editingInvoice.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
          } catch (error) {
            console.error("Note: Time entries may already be linked or not exist");
          }
        } else if (formData.project_id) {
          try {
            console.log(`🔗 Ensuring time entries are linked to invoice ${editingInvoice.id}`);
            await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${editingInvoice.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
          } catch (error) {
            console.error("Note: Time entries may already be linked or not exist");
          }
        }
      } else {
        console.log('🟡 Creating new invoice...');
        const response = await api.post(`/invoices`, data);
        console.log('🟡 Invoice created:', response.data.id);
        toast.success(t("common.create"));
        trackInvoiceCreated();

        // Feb 2026 — Wenn Inline-Kunde: nach Erstellung Promote-Dialog zeigen
        if (useInlineCustomer && response.data.customer_id) {
          setPromoteDialog({
            isOpen: true,
            customerId: response.data.customer_id,
            customerName: response.data.customer_name || inlineCustomer.name,
          });
        }
        
        // Mark time entries as invoiced if this was created from time tracking
        if (formData.project_ids?.length > 0) {
          try {
            const pids = formData.project_ids.join(",");
            console.log(`🔗 Linking time entries for projects [${pids}] to invoice ${response.data.id}`);
            await api.post(`/time-entries/mark-invoiced?project_ids=${pids}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
            console.log('✅ Time entries marked as invoiced');
          } catch (error) {
            console.error("❌ Error marking time entries:", error);
            toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
          }
        } else if (formData.project_id) {
          try {
            console.log(`🔗 Linking time entries to invoice ${response.data.id} with status ${data.status}`);
            await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
            console.log('✅ Time entries marked as invoiced');
          } catch (error) {
            console.error("❌ Error marking time entries:", error);
            toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
          }
        }
      }

      const wasCreate = !editingInvoice;
      setDialogOpen(false);
      setEditingInvoice(null);
      // Keep the current country from formData (which was set by useEffect)
      const currentCountry = formData.country;
      setFormData({
        customer_id: "",
        project_id: "",
        invoice_date: "",
        due_date: "",
        service_date: "",
        service_date_end: "",
        country: currentCountry,
        status: "created",
        notes: "",
        buyer_reference: "",
        items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
      });
      console.log('🟡 Calling fetchInvoices after save...');
      fetchInvoices();

      // Konto-sichern-Moment: nach der ersten eigenen Rechnung eines Gasts gezielt einblenden
      if (wasCreate && user?.is_guest) {
        const alreadyPrompted = localStorage.getItem("ivoliq_guest_claim_prompted") === "1";
        if (!alreadyPrompted) {
          localStorage.setItem("ivoliq_guest_claim_prompted", "1");
          setTimeout(() => setGuestClaimOpen(true), 600);
        }
      }
    } catch (error) {
      if (error.response?.status === 422 && typeof error.response?.data?.detail === 'object' && error.response?.data?.detail?.error === "QR_REQUIREMENTS_NOT_MET") {
        // Handle QR fallback requirement
        setQrFallbackData(error.response.data.detail);
        setQrFallbackModalOpen(true);
      } else if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        // Feb 2026 — Pydantic-Validation-Errors (422) werden vom api-Interceptor bereits
        // als menschenlesbarer String in detail geliefert. Fallback für String-Detail.
        const detail = error.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : 'Fehler beim Speichern der Rechnung';
        toast.error(msg);
      }
    }
  };

  const handleQrFallbackConfirm = async () => {
    try {
      const data = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        service_date: new Date(formData.service_date).toISOString(),
        service_date_end: formData.service_date_end ? new Date(formData.service_date_end).toISOString() : null,
        allow_fallback_without_qr: true,
        fallback_confirmed: true
      };

      console.log('🟡 Creating invoice with QR fallback confirmed...');
      const response = await api.post(`/invoices`, data);
      console.log('🟡 Invoice created with fallback:', response.data.id);
      toast.success("Rechnung ohne QR-Code erstellt");
      
      // Mark time entries as invoiced if this was created from time tracking
      if (formData.project_ids?.length > 0) {
        try {
          const pids = formData.project_ids.join(",");
          await api.post(`/time-entries/mark-invoiced?project_ids=${pids}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
        } catch (error) {
          toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
        }
      } else if (formData.project_id) {
        try {
          console.log(`🔗 Linking time entries to invoice ${response.data.id} with status ${data.status}`);
          await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
          console.log('✅ Time entries marked as invoiced');
        } catch (error) {
          console.error("❌ Error marking time entries:", error);
          toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
        }
      }

      setQrFallbackModalOpen(false);
      setQrFallbackData(null);
      setDialogOpen(false);
      setEditingInvoice(null);
      
      // Reset form
      const currentCountry = formData.country;
      setFormData({
        customer_id: "",
        project_id: "",
        invoice_date: "",
        due_date: "",
        service_date: "",
        service_date_end: "",
        country: currentCountry,
        status: "created",
        notes: "",
        buyer_reference: "",
        items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
      });
      
      fetchInvoices();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Fehler beim Erstellen der Rechnung';
      toast.error(msg);
    }
  };

  const handleQrFallbackCancel = () => {
    setQrFallbackModalOpen(false);
    setQrFallbackData(null);
  };

  const handlePreview = async () => {
    // Enhanced client-side validation before API call
    if (!useInlineCustomer && !formData.customer_id) {
      toast.error('Kunde ist erforderlich. Bitte wählen Sie einen Kunden aus der Liste oder erfassen Sie einen neuen.');
      return;
    }
    if (useInlineCustomer && !inlineCustomer.name.trim()) {
      toast.error('Bitte geben Sie einen Kundennamen ein.');
      return;
    }
    
    if (!formData.invoice_date) {
      toast.error('Rechnungsdatum ist erforderlich. Bitte geben Sie das Datum der Rechnungsstellung ein.');
      return;
    }
    
    if (!formData.due_date) {
      toast.error('Fälligkeitsdatum ist erforderlich. Bitte geben Sie das Zahlungsziel ein.');
      return;
    }
    
    if (!formData.service_date) {
      toast.error('Leistungsdatum ist erforderlich. Bitte geben Sie das Datum der erbrachten Leistung ein.');
      return;
    }
    
    // Validate that at least one item exists with description and price
    const validItems = formData.items.filter(item => 
      item.description && item.description.trim() && 
      item.quantity > 0 && 
      item.unit_price >= 0
    );
    
    if (validItems.length === 0) {
      toast.error('Mindestens eine Position ist erforderlich. Bitte fügen Sie eine Position mit Beschreibung, Menge und Preis hinzu.');
      return;
    }
    
    console.log('🔍 Preview validation passed, generating PDF preview:', {
      customer_id: formData.customer_id || '(inline)',
      items: validItems.length,
      service_date: formData.service_date
    });
    
    // Show loading state and generate preview first
    setIsLoading(true);
    
    try {
      // Process data the same way as create endpoint for consistency
      const data = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        service_date: new Date(formData.service_date).toISOString(),
        service_date_end: formData.service_date_end ? new Date(formData.service_date_end).toISOString() : null,
      };

      // Inline-Kunde an Preview-Endpoint mitgeben
      if (useInlineCustomer) {
        data.customer_id = undefined;
        data.inline_customer = { ...inlineCustomer };
      }

      // Make API call to generate preview PDF
      const response = await api.post('/invoices/preview', data, {
        responseType: 'blob'
      });
      
      console.log('✅ Preview API response received, size:', response.data.size);
      
      // Create blob URL for PDF display
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const previewUrl = URL.createObjectURL(blob);
      
      // Only open modal after successful PDF generation
      setPreviewPdfUrl(previewUrl);
      setPreviewModalOpen(true);
      
    } catch (error) {
      console.error('❌ Preview generation failed:', error);
      
      // Enhanced error handling with specific German messages
      let errorMessage = 'Fehler beim Generieren der Rechnungsvorschau';
      
      if (error.response) {
        const status = error.response.status;
        
        console.error('❌ Preview error details:', { status, data: error.response.data });
        
        if (status === 401) {
          errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
        } else if (status === 404) {
          errorMessage = 'Kunde nicht gefunden. Bitte wählen Sie einen gültigen Kunden aus.';
        } else if (status === 422) {
          errorMessage = 'Ungültige Rechnungsdaten. Bitte überprüfen Sie alle Pflichtfelder und versuchen Sie es erneut.';
        } else if (status === 500) {
          errorMessage = 'Serverfehler beim Generieren der Vorschau. Bitte versuchen Sie es später erneut.';
        } else {
          // Try to extract error message from response
          try {
            if (error.response.data instanceof Blob) {
              const errorText = await error.response.data.text();
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.detail || errorMessage;
            } else if (error.response.data?.detail) {
              errorMessage = error.response.data.detail;
            }
          } catch (parseError) {
            console.error('Could not parse error response:', parseError);
          }
        }
      } else if (error.request) {
        errorMessage = 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
        console.error('❌ Network error:', error.request);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewEdit = () => {
    // Cleanup preview URL and close modal
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setPreviewModalOpen(false);
  };

  const handlePreviewCreate = async () => {
    // Execute the same logic as handleSubmit but without the event
    try {
      const data = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        service_date: new Date(formData.service_date).toISOString(),
        service_date_end: formData.service_date_end ? new Date(formData.service_date_end).toISOString() : null,
      };

      // Feb 2026 — Inline-Kunde mitschicken statt customer_id
      if (useInlineCustomer) {
        data.customer_id = undefined;
        data.inline_customer = { ...inlineCustomer };
      }

      console.log('🟡 Creating new invoice from preview...');
      const response = await api.post(`/invoices`, data);
      console.log('🟡 Invoice created:', response.data.id);
      toast.success(t("common.create"));
      trackInvoiceCreated();

      // Feb 2026 — Wenn Inline-Kunde: nach Erstellung Promote-Dialog zeigen
      if (useInlineCustomer && response.data.customer_id) {
        setPromoteDialog({
          isOpen: true,
          customerId: response.data.customer_id,
          customerName: response.data.customer_name || inlineCustomer.name,
        });
      }
      
      // Mark time entries as invoiced if this was created from time tracking
      if (formData.project_ids?.length > 0) {
        try {
          const pids = formData.project_ids.join(",");
          await api.post(`/time-entries/mark-invoiced?project_ids=${pids}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
        } catch (error) {
          toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
        }
      } else if (formData.project_id) {
        try {
          console.log(`🔗 Linking time entries to invoice ${response.data.id} with status ${data.status}`);
          await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${response.data.id}&invoice_status=${data.status}${billUntil ? `&until=${billUntil}` : ''}`);
            fetchTimeEntries();
          console.log('✅ Time entries marked as invoiced');
        } catch (error) {
          console.error("❌ Error marking time entries:", error);
          toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
        }
      }

      // Close dialogs and reset form
      setDialogOpen(false);
      
      // Cleanup preview URL and close modal
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
      }
      setPreviewModalOpen(false);
      setEditingInvoice(null);
      
      // Keep the current country from formData
      const currentCountry = formData.country;
      setFormData({
        customer_id: "",
        project_id: "",
        invoice_date: "",
        due_date: "",
        service_date: "",
        service_date_end: "",
        country: currentCountry,
        status: "created",
        notes: "",
        buyer_reference: "",
        items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
      });
      
      console.log('🟡 Calling fetchInvoices after preview create...');
      fetchInvoices();
      
    } catch (error) {
      console.error('❌ Invoice creation from preview failed:', error);
      
      // Enhanced error handling with specific German messages
      let errorMessage = 'Fehler beim Erstellen der Rechnung';
      
      if (error.response) {
        const status = error.response.status;
        
        if (status === 401) {
          errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
        } else if (status === 422) {
          if (error.response.data?.detail?.error === "QR_REQUIREMENTS_NOT_MET") {
            // Handle QR fallback requirement
            setQrFallbackData(error.response.data.detail);
            setQrFallbackModalOpen(true);
            setPreviewModalOpen(false); // Close preview modal
            return; // Don't show error toast for QR fallback
          } else {
            errorMessage = 'Ungültige Rechnungsdaten. Bitte überprüfen Sie alle Felder.';
          }
        } else if (status === 403) {
          setUpgradeMessage(error.response.data.detail);
          setUpgradeModalOpen(true);
          setPreviewModalOpen(false); // Close preview modal
          return; // Don't show error toast for upgrade prompt
        } else if (status === 404) {
          errorMessage = 'Kunde oder Projekt nicht gefunden.';
        } else if (status === 500) {
          errorMessage = 'Serverfehler beim Erstellen der Rechnung. Bitte versuchen Sie es später erneut.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        errorMessage = 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      }
      
      toast.error(errorMessage);
      throw error; // Re-throw to let preview modal handle it
    }
  };

  const handleOpenSettings = () => {
    setQrFallbackModalOpen(false);
    setQrFallbackData(null);
    navigate('/settings');
  };

  const deleteInvoice = async (id) => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("common.deleted"));
      fetchInvoices();
      setConfirmDialog({ isOpen: false, type: null, invoiceId: null, title: '', description: '' });
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Error deleting invoice");
      }
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({
      isOpen: true,
      type: 'delete',
      invoiceId: id,
      title: t('invoices.confirmDelete', 'Rechnung löschen?'),
      description: t('invoices.confirmDeleteDescription', 'Diese Aktion kann nicht rückgängig gemacht werden.')
    });
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    // Optimistic Update: lokalen State sofort anpassen, kein full re-fetch
    const prevInvoices = invoices;
    setInvoices(list => list.map(inv => inv.id === invoiceId
      ? { ...inv, status: newStatus, paid_at: newStatus === 'paid' && !inv.paid_at ? new Date().toISOString() : (newStatus !== 'paid' ? null : inv.paid_at) }
      : inv
    ));
    try {
      const res = await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      // Backend-Response verwenden (authoritative); setzt auch Derived-Fields
      if (res?.data?.id) {
        setInvoices(list => list.map(inv => inv.id === invoiceId ? { ...inv, ...res.data } : inv));
      }
      toast.success(t('common.updated'));
    } catch (error) {
      // Rollback bei Fehler
      setInvoices(prevInvoices);
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else if (error.response?.status === 423) {
        const detail = error.response.data?.detail;
        toast.error(typeof detail === 'object' ? detail.message : (detail || 'Periode festgeschrieben (GoBD)'));
      } else {
        toast.error(t('common.error'));
      }
    }
  };

  const updateInvoicePaidAt = async (invoiceId, newDate) => {
    // Optimistic Update: paid_at + ggf. status=paid sofort lokal anpassen
    const isoDate = newDate ? `${newDate}T12:00:00` : null;
    const prevInvoices = invoices;
    setInvoices(list => list.map(inv => inv.id === invoiceId
      ? { ...inv, paid_at: isoDate, status: isoDate ? 'paid' : (inv.status === 'paid' ? 'sent' : inv.status) }
      : inv
    ));
    try {
      const res = await api.put(`/invoices/${invoiceId}`, { paid_at: isoDate });
      if (res?.data?.id) {
        setInvoices(list => list.map(inv => inv.id === invoiceId ? { ...inv, ...res.data } : inv));
      }
      toast.success('Bezahldatum aktualisiert');
    } catch (error) {
      setInvoices(prevInvoices);
      if (error.response?.status === 423) {
        const detail = error.response.data?.detail;
        toast.error(typeof detail === 'object' ? detail.message : (detail || 'Periode festgeschrieben (GoBD)'));
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
      }
    }
  };

  const resetInvoiceStatus = async (invoiceId) => {
    const prevInvoices = invoices;
    setInvoices(list => list.map(inv => inv.id === invoiceId ? { ...inv, status: 'created', paid_at: null } : inv));
    try {
      const res = await api.put(`/invoices/${invoiceId}`, { status: "created" });
      if (res?.data?.id) {
        setInvoices(list => list.map(inv => inv.id === invoiceId ? { ...inv, ...res.data } : inv));
      }
      toast.success(t('invoices.resetToCreatedSuccess', 'Status reset to created'));
      setConfirmDialog({ isOpen: false, type: null, invoiceId: null, title: '', description: '' });
    } catch (error) {
      setInvoices(prevInvoices);
      toast.error(t('invoices.resetError', 'Error resetting'));
    }
  };

  const handleResetClick = (id) => {
    setConfirmDialog({
      isOpen: true,
      type: 'reset',
      invoiceId: id,
      title: t('invoices.confirmResetCreated', 'Status zurücksetzen?'),
      description: t('invoices.confirmResetDescription', 'Der Status wird auf "Erstellt" zurückgesetzt.')
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'delete') {
      deleteInvoice(confirmDialog.invoiceId);
    } else if (confirmDialog.type === 'reset') {
      resetInvoiceStatus(confirmDialog.invoiceId);
    }
  };

  const handleSendEmail = (invoice) => {
    setSelectedInvoiceForEmail(invoice);
    setEmailModalOpen(true);
  };

  const handleEmailModalClose = () => {
    setEmailModalOpen(false);
    setSelectedInvoiceForEmail(null);
  };

  const getFilenameFromHeaders = (headers) => {
    const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"];
    if (!disposition) return null;
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
    const filename = match?.[1] || match?.[2];
    if (!filename) return null;
    try {
      return decodeURIComponent(filename);
    } catch (error) {
      return filename;
    }
  };

  const downloadPDF = async (invoiceId) => {
    try {
      console.log('📄 Downloading PDF for invoice:', invoiceId);
      // Feb 2026 — Cache-Buster: ohne Query-Param liefert ein Proxy/CDN/Browser
      // u.U. die alte PDF zurück, auch wenn das Backend frische Werte gerendert hat
      // (siehe User-Bugreport „PDF nach Edit unverändert").
      const cacheBust = Date.now();
      const response = await api.get(`/invoices/${invoiceId}/pdf?_=${cacheBust}`, {
        responseType: "blob"
      });
      console.log('📄 PDF received, size:', response.data.size);
      const filename = getFilenameFromHeaders(response.headers) || `Rechnung_${invoiceId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("invoices.downloadPDF"));
    } catch (error) {
      console.error('📄 PDF download error:', error);
      
      // Handle error response when responseType is blob
      let errorMessage = error.message;
      
      if (error.response) {
        // If response data is a blob (due to responseType: "blob"), try to read it as text
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorText;
          } catch (parseError) {
            // If parsing fails, use status text or generic message
            errorMessage = error.response.statusText || `HTTP ${error.response.status}`;
          }
        } else {
          // Normal JSON response
          errorMessage = error.response.data?.detail || error.response.statusText || error.message;
        }
      }
      
      toast.error(`PDF-Download fehlgeschlagen: ${errorMessage}`);
    }
  };

  const downloadXRechnung = async (invoiceId) => {
    try {
      console.log('📄 Downloading XRechnung for invoice:', invoiceId);
      const response = await api.post(`/invoices/${invoiceId}/export-xrechnung`, {}, {
        responseType: "blob"
      });
      console.log('📄 XRechnung received, size:', response.data.size);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `xrechnung_${invoiceId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("XRechnung erfolgreich heruntergeladen");
    } catch (error) {
      console.error('📄 XRechnung download error:', error);
      
      // Handle error response when responseType is blob
      let errorMessage = error.message;
      
      if (error.response) {
        // If response data is a blob (due to responseType: "blob"), try to read it as text
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorText;
          } catch (parseError) {
            // If parsing fails, use status text or generic message
            errorMessage = error.response.statusText || `HTTP ${error.response.status}`;
          }
        } else {
          // Normal JSON response
          errorMessage = error.response.data?.detail || error.response.statusText || error.message;
        }
      }
      
      toast.error(`XRechnung-Export fehlgeschlagen: ${errorMessage}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      created: { label: t("invoices.status.created"), className: "bg-blue-50 text-blue-700" },
      draft: { label: t("invoices.status.draft"), className: "bg-gray-100 text-gray-800" },
      sent: { label: t("invoices.status.sent"), className: "bg-blue-100 text-blue-800" },
      paid: { label: t("invoices.status.paid"), className: "bg-green-100 text-green-800" },
      cancelled: { label: t("invoices.status.cancelled"), className: "bg-red-100 text-red-800" },
    };
    const config = statusConfig[status] || statusConfig.created;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getDueDateStatus = (dueDate, status) => {
    // Don't show due date status for paid or cancelled invoices
    if (status === 'paid' || status === 'cancelled') {
      return null;
    }

    if (!dueDate) {
      return null;
    }

    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let className = "text-xs";
    let text = `Zu zahlen bis ${format(due, "dd.MM.yyyy", { locale })}`;

    if (diffDays < 0) {
      // Overdue - red
      className += " text-red-600 font-medium";
      const overdueDays = Math.abs(diffDays);
      text = `Überfällig seit ${overdueDays} Tag${overdueDays === 1 ? '' : 'en'}`;
    } else if (diffDays <= 7) {
      // Due within 7 days - yellow/orange
      className += " text-orange-600 font-medium";
      if (diffDays === 0) {
        text = "Heute fällig";
      } else if (diffDays === 1) {
        text = "Morgen fällig";
      } else {
        text = `Fällig in ${diffDays} Tagen`;
      }
    } else {
      // More than 7 days - neutral gray
      className += " text-gray-600";
    }

    return (
      <div className={className}>
        {text}
      </div>
    );
  };

  const isKU = user?.is_kleinunternehmer === true;

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTaxTotal = () => {
    if (isKU) return 0;
    // Feb 2026 — bei Cross-Border-Szenarien (RC, IGL, Export) ist USt per Gesetz 0,
    // unabhängig davon, was gerade in items[].tax_rate steht (Race-Condition mit dem
    // Auto-Sync-useEffect möglich). Single Source of Truth: tax_scenario.
    const sellerCountry = (user?.country || 'DE').toUpperCase();
    const selectedCustomer = useInlineCustomer
      ? inlineCustomer
      : customers.find(c => c.id === formData.customer_id);
    const customerCountry = (selectedCustomer?.country_code || '').toUpperCase();
    const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
    if (isCrossBorder) {
      const scenario = formData.tax_scenario || detectTaxScenario();
      if (scenario && scenario !== 'standard') return 0;
    }
    return formData.items.reduce((sum, item) => sum + (item.total * item.tax_rate / 100), 0);
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("invoices.title")} {selectedYear}
          </h1>
          <p className="text-sm text-slate-500">
            {t("invoices.subtitle")} für das Jahr {selectedYear}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <YearSelector 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            minYear={2020}
            maxYear={new Date().getFullYear() + 1}
          />
          <div className="flex flex-wrap gap-2">
          <Dialog open={autoCreateDialogOpen} onOpenChange={(open) => { setAutoCreateDialogOpen(open); if (open) setBillUntil(""); if (!open) setSelectedProjectsForAuto([]); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="auto-create-invoice-button">
                <Clock className="h-4 w-4 mr-2" />
                Aus Zeiteinträgen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl" data-testid="auto-create-dialog-title">Rechnung aus Zeiteinträgen</DialogTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Wählen Sie ein oder mehrere Projekte aus. Jedes Projekt wird als eigene Position auf der Rechnung angelegt.
                </p>
              </DialogHeader>
              <div className="space-y-5 mt-4">
                {/* Teilabrechnung: Zeitraum-Cutoff (Sep 2026) */}
                <div className="bg-gray-50 border rounded-xl p-3 space-y-2" data-testid="bill-until-section">
                  <Label className="text-sm font-semibold text-gray-700">Abrechnen bis (einschließlich)</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input type="date" value={billUntil} onChange={(e) => applyBillUntil(e.target.value)}
                      className="w-40 h-9" data-testid="bill-until-input" />
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => applyBillUntil(format(new Date(new Date().getFullYear(), new Date().getMonth(), 0), "yyyy-MM-dd"))}
                      data-testid="bill-until-prev-month">
                      Bis Ende letzten Monats
                    </Button>
                    <Button type="button" size="sm" variant={billUntil ? "outline" : "secondary"}
                      onClick={() => applyBillUntil("")} data-testid="bill-until-all">
                      Alle offenen
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500" data-testid="bill-until-hint">
                    {billUntil
                      ? `Nur Zeiteinträge bis ${format(new Date(billUntil + "T12:00:00"), "dd.MM.yyyy")} werden abgerechnet — spätere Stunden bleiben offen für die nächste Rechnung.`
                      : "Alle offenen Zeiteinträge werden abgerechnet."}
                  </p>
                </div>
                {/* Selected projects list */}
                {selectedProjectsForAuto.length > 0 && (
                  <div className="space-y-2" data-testid="selected-projects-list">
                    <Label className="text-sm font-semibold text-gray-700">Ausgewählte Projekte</Label>
                    {selectedProjectsForAuto.map((sp) => {
                      const currency = user?.country === 'CH' ? 'CHF' : '€';
                      const amount = sp.totalHours * sp.hourly_rate;
                      return (
                        <div key={sp.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid={`selected-project-${sp.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{sp.name}</div>
                            <div className="text-xs text-gray-600">
                              Kunde: {sp.customerName} · {sp.totalHours.toFixed(1)}h × {sp.hourly_rate}{currency}/h = <span className="font-semibold">{amount.toFixed(2)}{currency}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeProjectFromAutoSelection(sp.id)}
                            className="ml-3 p-1 text-gray-400 hover:text-red-500 transition-colors"
                            data-testid={`remove-project-${sp.id}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                    {/* Summary */}
                    {selectedProjectsForAuto.length > 1 && (() => {
                      const currency = user?.country === 'CH' ? 'CHF' : '€';
                      const totalH = selectedProjectsForAuto.reduce((s, p) => s + p.totalHours, 0);
                      const totalA = selectedProjectsForAuto.reduce((s, p) => s + p.totalHours * p.hourly_rate, 0);
                      return (
                        <div className="flex justify-between items-center px-4 py-2 bg-gray-50 rounded-xl border text-sm font-medium" data-testid="multi-project-summary">
                          <span>Gesamt: {selectedProjectsForAuto.length} Projekte · {totalH.toFixed(1)}h</span>
                          <span className="text-emerald-700">{totalA.toFixed(2)}{currency}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Add project selector */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    {selectedProjectsForAuto.length === 0 ? "Projekt auswählen" : "+ Weiteres Projekt hinzufügen"}
                  </Label>
                  <Select
                    value=""
                    onValueChange={addProjectToAutoSelection}
                  >
                    <SelectTrigger data-testid="auto-project-select" className="h-12">
                      <SelectValue placeholder="Projekt mit offenen Zeiteinträgen wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.filter(p => {
                        // Exclude already-selected projects
                        if (selectedProjectsForAuto.find(sp => sp.id === p.id)) return false;
                        const openEntries = timeEntries.filter(e => 
                          e.project_id === p.id && 
                          (e.net_duration_minutes || e.duration_minutes) && 
                          !e.invoiced
                        );
                        return openEntries.length > 0;
                      }).map((project) => {
                        const projectEntries = timeEntries.filter(
                          e => e.project_id === project.id && 
                               (e.net_duration_minutes || e.duration_minutes) && 
                               !e.invoiced
                        );
                        const totalHours = projectEntries.reduce((sum, e) => sum + (e.net_duration_minutes || e.duration_minutes), 0) / 60;
                        const totalAmount = totalHours * project.hourly_rate;
                        const currency = user?.country === 'CH' ? 'CHF' : '€';
                        const customer = customers.find(c => c.id === project.customer_id);
                        return (
                          <SelectItem key={project.id} value={project.id}>
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">{project.name}</span>
                              <span className="text-xs text-gray-600">
                                {customer?.name || "–"} · {totalHours.toFixed(1)}h × {project.hourly_rate}{currency}/h = {totalAmount.toFixed(2)}{currency}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                      {projects.filter(p => {
                        if (selectedProjectsForAuto.find(sp => sp.id === p.id)) return false;
                        const openEntries = timeEntries.filter(e => 
                          e.project_id === p.id && 
                          (e.net_duration_minutes || e.duration_minutes) && 
                          !e.invoiced
                        );
                        return openEntries.length > 0;
                      }).length === 0 && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {selectedProjectsForAuto.length > 0 
                            ? "Keine weiteren Projekte mit offenen Zeiteinträgen" 
                            : "Keine Projekte mit offenen Zeiteinträgen gefunden"}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {selectedProjectsForAuto.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Die Rechnung wird als Entwurf erstellt. Sie können sie vor dem Versand noch anpassen.
                  </p>
                )}

                <Button 
                  onClick={createInvoiceFromTimeEntries} 
                  className="w-full h-12 text-base"
                  data-testid="confirm-auto-create"
                  disabled={selectedProjectsForAuto.length === 0}
                >
                  <FileText className="h-5 w-5 mr-2" />
                  {selectedProjectsForAuto.length > 1 
                    ? `Rechnung aus ${selectedProjectsForAuto.length} Projekten erstellen`
                    : "Rechnung jetzt erstellen"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Customer mismatch warning dialog */}
          <Dialog open={!!customerMismatchWarning} onOpenChange={(open) => { if (!open) setCustomerMismatchWarning(null); }}>
            <DialogContent className="max-w-md" data-testid="customer-mismatch-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Unterschiedliche Kunden
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <p className="text-sm text-gray-700">
                  Das Projekt <span className="font-semibold">{customerMismatchWarning?.projectToAdd?.name}</span> gehört 
                  zu Kunde <span className="font-semibold">{customerMismatchWarning?.newCustomerName}</span>, 
                  aber die bisherigen Projekte gehören zu <span className="font-semibold">{customerMismatchWarning?.existingCustomerName}</span>.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  Eine Rechnung mit Positionen verschiedener Kunden kann steuerlich problematisch sein. Prüfen Sie, ob dies beabsichtigt ist.
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setCustomerMismatchWarning(null)}
                    data-testid="cancel-mismatch"
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    onClick={confirmCustomerMismatch}
                    data-testid="confirm-mismatch"
                  >
                    Trotzdem hinzufügen
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual create dialog */}
          <Dialog open={dialogOpen} onOpenChange={handleInvoiceDialogOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-button" onClick={openNewInvoiceDialog}>
                <Plus className="h-4 w-4 mr-2" />
                {t("invoices.newInvoice")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {editingInvoice ? "📝 Rechnung bearbeiten" : "📄 Neue Rechnung erstellen"}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                {editingInvoice 
                  ? t('invoices.editDetails', 'Edit your invoice details.') 
                  : t('invoices.createInvoiceHelp', 'Create a new invoice for your client. All fields marked with * are required.')}
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="bg-gray-50 border rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">📍 Rechnungsempfänger</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.customer")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Wählen Sie den Kunden für diese Rechnung</p>
                    <Select
                      value={useInlineCustomer ? "__inline__" : formData.customer_id}
                      onValueChange={(value) => {
                        if (value === "__inline__") {
                          setUseInlineCustomer(true);
                          // Kunde + Projekt zurücksetzen, Inline-Form öffnen
                          setFormData({ ...formData, customer_id: "", project_id: "" });
                        } else {
                          setUseInlineCustomer(false);
                          // Wenn aktuelles Projekt nicht zu neuem Kunden gehört → Projekt leeren
                          const proj = projects.find((p) => p.id === formData.project_id);
                          const keepProject = proj && proj.customer_id === value;
                          setFormData({ ...formData, customer_id: value, project_id: keepProject ? formData.project_id : "" });
                        }
                      }}
                      required={!useInlineCustomer}
                    >
                      <SelectTrigger data-testid="invoice-customer-select" className="h-10">
                        <SelectValue placeholder={t('invoices.selectCustomer', 'Select client...')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__inline__" data-testid="invoice-inline-customer-option">
                          ✚ Neuen Kunden eingeben (nur für diese Rechnung)
                        </SelectItem>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.project")} (optional)</Label>
                    <p className="text-xs text-gray-600 mb-2">
                      {formData.customer_id
                        ? "Nur Projekte des gewählten Kunden"
                        : useInlineCustomer
                        ? "Bei neuen Kunden noch kein Projekt verknüpfbar"
                        : "Optional: Projekt setzt automatisch den Kunden"}
                    </p>
                    <Select
                      value={formData.project_id}
                      onValueChange={(value) => {
                        // Wenn ein Projekt gewählt wird ohne Kunde → Kunde automatisch setzen
                        const proj = projects.find((p) => p.id === value);
                        if (proj && proj.customer_id && !formData.customer_id && !useInlineCustomer) {
                          setFormData({ ...formData, project_id: value, customer_id: proj.customer_id });
                        } else {
                          setFormData({ ...formData, project_id: value });
                        }
                      }}
                      disabled={useInlineCustomer}
                    >
                      <SelectTrigger data-testid="invoice-project-select" className="h-10">
                        <SelectValue placeholder="Kein Projekt" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects
                          .filter((project) =>
                            !formData.customer_id ? true : project.customer_id === formData.customer_id
                          )
                          .map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Inline-Kunde-Formular: aufklappbar, wenn "Neuen Kunden eingeben" gewählt */}
                {useInlineCustomer && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-emerald-200" data-testid="invoice-inline-customer-form">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm text-emerald-700">Neuer Kunde (nur für diese Rechnung)</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          setUseInlineCustomer(false);
                          setInlineCustomer({ ...INLINE_CUSTOMER_DEFAULT, country_code: formData.country || "DE" });
                        }}
                        data-testid="invoice-inline-customer-cancel"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Abbrechen
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Nach Speichern fragen wir, ob du den Kunden in deine Kunden-DB übernehmen möchtest.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-xs">Name / Firma *</Label>
                        <Input
                          value={inlineCustomer.name}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, name: e.target.value })}
                          placeholder="z.B. Mustermann GmbH"
                          required
                          data-testid="invoice-inline-customer-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">E-Mail</Label>
                        <Input
                          type="email"
                          value={inlineCustomer.email}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, email: e.target.value })}
                          data-testid="invoice-inline-customer-email"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Telefon</Label>
                        <Input
                          value={inlineCustomer.phone}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, phone: e.target.value })}
                          data-testid="invoice-inline-customer-phone"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Straße + Hausnummer</Label>
                        <div className="grid grid-cols-[1fr_80px] gap-2">
                          <Input
                            value={inlineCustomer.street}
                            onChange={(e) => setInlineCustomer({ ...inlineCustomer, street: e.target.value })}
                            placeholder="Straße"
                            data-testid="invoice-inline-customer-street"
                          />
                          <Input
                            value={inlineCustomer.street_number}
                            onChange={(e) => setInlineCustomer({ ...inlineCustomer, street_number: e.target.value })}
                            placeholder="Nr."
                            data-testid="invoice-inline-customer-street-number"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">PLZ</Label>
                        <Input
                          value={inlineCustomer.postal_code}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, postal_code: e.target.value })}
                          data-testid="invoice-inline-customer-postal"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Stadt</Label>
                        <Input
                          value={inlineCustomer.city}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, city: e.target.value })}
                          data-testid="invoice-inline-customer-city"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Land *</Label>
                        <input
                          list="invoice-country-suggestions"
                          value={inlineCustomer.country_code}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, country_code: e.target.value.toUpperCase().slice(0, 2) })}
                          data-testid="invoice-inline-customer-country"
                          maxLength={2}
                          required
                          placeholder="z.B. DE, AT, FR…"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                        />
                        <datalist id="invoice-country-suggestions">
                          <option value="DE">Deutschland</option>
                          <option value="AT">Österreich</option>
                          <option value="CH">Schweiz</option>
                          <option value="FR">Frankreich</option>
                          <option value="IT">Italien</option>
                          <option value="NL">Niederlande</option>
                          <option value="ES">Spanien</option>
                          <option value="PL">Polen</option>
                          <option value="US">USA</option>
                          <option value="GB">Großbritannien</option>
                        </datalist>
                        <span className="text-[11px] text-gray-400">Vorschläge wählen oder eigenen ISO-Code eingeben</span>
                      </div>
                      <div>
                        <Label className="text-xs">USt-IdNr. (optional)</Label>
                        <Input
                          value={inlineCustomer.vat_id}
                          onChange={(e) => setInlineCustomer({ ...inlineCustomer, vat_id: e.target.value })}
                          placeholder={`z.B. ${getVatIdPlaceholder(inlineCustomer.country_code || user?.country || 'DE')}`}
                          data-testid="invoice-inline-customer-vat"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">📅 Rechnungsdaten</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.invoiceDate")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Datum der Rechnungsstellung</p>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                      className="h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.dueDate")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Zahlungsziel — Tage eingeben oder Datum wählen</p>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        className="h-10 w-24"
                        data-testid="invoice-due-days"
                        placeholder="Tage"
                        value={(() => {
                          if (!formData.invoice_date || !formData.due_date) return "";
                          const diff = Math.round((new Date(formData.due_date) - new Date(formData.invoice_date)) / 86400000);
                          return Number.isFinite(diff) ? diff : "";
                        })()}
                        onChange={(e) => {
                          const d = parseInt(e.target.value, 10);
                          if (!Number.isFinite(d) || d < 0) return;
                          const base = formData.invoice_date ? new Date(formData.invoice_date) : new Date();
                          setFormData({ ...formData, due_date: format(new Date(base.getTime() + d * 86400000), "yyyy-MM-dd") });
                        }}
                      />
                      <Input
                        type="date"
                        data-testid="invoice-due-date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        required
                        className="h-10 flex-1"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Tage ab Rechnungsdatum — das Datum springt automatisch mit</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t('invoices.serviceDate', 'Service Date')} *</Label>
                    <Input
                      id="service_date"
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                      className="h-10"
                      max={formData.invoice_date || new Date().toISOString().split('T')[0]}
                      title={t('invoices.serviceDateNote', 'Service date cannot be after invoice date')}
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold">{t('invoices.serviceDateEnd', 'Service End Date')} ({t('common.optional', 'optional')})</Label>
                    <Input
                      id="service_date_end"
                      type="date"
                      value={formData.service_date_end || ''}
                      onChange={(e) => setFormData({ ...formData, service_date_end: e.target.value })}
                      className="h-10"
                      min={formData.service_date || ''}
                      max={formData.invoice_date || new Date().toISOString().split('T')[0]}
                      title={t('invoices.serviceDateEndNote', 'End date must be after start date')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('invoices.servicePeriodHelp', 'Leave empty for single date, fill for period')}
                    </p>
                  </div>
                </div>
              </div>

              {/* BuyerReference (BT-10) Field */}
              <div className="bg-gray-50 border rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">🏛️ Behörden & E-Rechnung</h3>
                <div>
                  <Label className="text-sm font-semibold">Leitweg-ID / Buyer Reference (BT-10)</Label>
                  <p className="text-xs text-gray-600 mb-2">
                    Für Rechnungen an öffentliche Auftraggeber (Behörden, Kommunen) erforderlich. 
                    Wird für XRechnung-Export verwendet.
                  </p>
                  <Input
                    type="text"
                    value={formData.buyer_reference || ''}
                    onChange={(e) => setFormData({ ...formData, buyer_reference: e.target.value })}
                    placeholder="z.B. 991-12345-12"
                    className="h-10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Nur bei Rechnungen an öffentliche Auftraggeber erforderlich. Lassen Sie das Feld leer für B2B/B2C-Rechnungen.
                  </p>
                </div>
              </div>

              {/* Feb 2026 — Outbound Steuerart-Block (Cross-Border-Pflicht: EU-RC, EU-IGL, Drittland-Export, Drittland-RC).
                  Wichtig:
                  • Card erscheint NUR bei Cross-Border (seller_country ≠ customer_country).
                  • Bei Kleinunternehmer (isKU) entfällt sie komplett — gar keine USt-Pflicht.
                  • Rechtshinweise sind länderspezifisch (DE: § 13b UStG / § 4 UStG; AT: § 19 UStG 1994 / Art. 6 + 7 UStG 1994).
                  • Bei Auswahl ≠ Standard werden alle Item-VAT-Sätze automatisch auf 0 % gesetzt (User muss nicht selber drandenken). */}
              {(() => {
                if (isKU) return null;  // Kleinunternehmer → keine Steuerart-Card
                const sellerCountry = (user?.country || 'DE').toUpperCase();
                const selectedCustomer = useInlineCustomer
                  ? inlineCustomer
                  : customers.find(c => c.id === formData.customer_id);
                const customerCountry = (selectedCustomer?.country_code || '').toUpperCase();
                // Cross-Border = Seller-Land ≠ Customer-Land
                const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
                if (!isCrossBorder) return null;  // Inland → keine Card

                const autoDetected = detectTaxScenario();
                const scenario = formData.tax_scenario || autoDetected;
                const setScenario = (v) => {
                  setFormData(prev => {
                    const updates = { ...prev, tax_scenario: v };
                    // Auto-Action: bei Cross-Border-Szenarien alle Item-Sätze auf 0 setzen (User muss nicht manuell)
                    if (v !== "standard") {
                      updates.items = (prev.items || []).map(it => ({
                        ...it,
                        tax_rate: 0,
                        total: Number(it.quantity || 0) * Number(it.unit_price || 0)
                      }));
                    }
                    return updates;
                  });
                };

                // Country-spezifische Labels & Rechtshinweise
                // WICHTIG (Compliance): Die "Legal Notes" sind die Texte, die per Gesetz
                // wortgleich auf der PDF-Rechnung erscheinen MÜSSEN. Sie kommen aus dem
                // Backend-Modul `app/invoicing/statements.py` (Single Source of Truth) und
                // werden vom PDF-Renderer und XRechnung-Export 1:1 verwendet. Änderungen
                // an dieser Stelle MÜSSEN auch dort gespiegelt werden, sonst zerfällt die
                // 100%-Identität (User-Auftrag Feb 2026).
                const isAT = sellerCountry === 'AT';
                const rcLabel = isAT ? "Reverse Charge (§ 19 Abs. 1a UStG 1994)" : "Reverse Charge (§ 13b UStG)";
                const iglLabel = isAT ? "Innergem. Lieferung (Art. 6 Abs. 1 UStG 1994)" : "Innergem. Lieferung (§ 4 Nr. 1b UStG)";
                const exportLabel = isAT ? "Drittland-Export (§ 7 UStG 1994)" : "Drittland-Export (§ 4 Nr. 1a UStG)";
                const rcLegal = isAT
                  ? "Steuerschuldnerschaft des Leistungsempfängers gemäß § 19 Abs. 1a UStG 1994."
                  : "Steuerschuldnerschaft des Leistungsempfängers gemäß § 13b UStG.";
                const iglLegal = isAT
                  ? "Steuerfreie innergemeinschaftliche Lieferung gemäß Art. 6 Abs. 1 UStG 1994."
                  : "Steuerfreie innergemeinschaftliche Lieferung gemäß § 4 Nr. 1b i.V.m. § 6a UStG.";
                const exportLegal = isAT
                  ? "Steuerfreie Ausfuhrlieferung gemäß § 7 UStG 1994."
                  : "Steuerfreie Ausfuhrlieferung gemäß § 4 Nr. 1a i.V.m. § 6 UStG.";

                const opts = [
                  { value: "standard", label: "Standard (mit USt)", desc: "Normaler USt-Ausweis – ungewöhnlich bei Cross-Border" },
                  { value: "reverse_charge", label: rcLabel, desc: "B2B-Dienstleistung – Empfänger schuldet die USt in seinem Land" },
                  { value: "intra_community", label: iglLabel, desc: "B2B-Warenlieferung in ein EU-Land (Kunde muss USt-IdNr. haben)" },
                  { value: "export", label: exportLabel, desc: "Warenlieferung in Drittland (USA, UK, CH, ...) – steuerfrei" },
                ];

                const legalNoteForPdf =
                  scenario === "reverse_charge" ? rcLegal
                  : scenario === "intra_community" ? iglLegal
                  : scenario === "export" ? exportLegal
                  : null;

                const customerLabel = selectedCustomer?.company_name || selectedCustomer?.name || "Kunde";

                return (
                  <div className="border-2 border-blue-300 bg-blue-50/60 rounded-xl p-4" data-testid="invoice-tax-scheme-card">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Steuerart dieser Rechnung</div>
                        <div className="text-[11px] text-slate-600 mt-0.5">
                          Cross-Border erkannt: <strong>{customerLabel}</strong> ({customerCountry}) ≠ dein Sitz ({sellerCountry}).
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {opts.map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-2 rounded-md border-2 p-2.5 cursor-pointer text-xs ${scenario === opt.value ? "border-blue-500 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}
                          data-testid={`invoice-tax-scheme-${opt.value}`}
                        >
                          <input
                            type="radio"
                            checked={scenario === opt.value}
                            onChange={() => setScenario(opt.value)}
                            className="mt-0.5"
                          />
                          <div>
                            <div className="font-medium text-slate-800">{opt.label}</div>
                            <div className="text-slate-500 mt-0.5 leading-snug">{opt.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {legalNoteForPdf && (
                      <div className="mt-3 pt-3 border-t border-slate-200" data-testid="invoice-tax-scheme-legal-note">
                        <LegalStatementPreview
                          country={sellerCountry}
                          scenario={scenario}
                          isKleinunternehmer={false}
                          variant="card"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="border rounded-xl p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <Label className="text-base font-semibold">{t("invoices.items")}</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Fügen Sie die Positionen Ihrer Rechnung hinzu (Leistungen, Produkte, Stunden, etc.)
                    </p>
                  </div>
                  <Button type="button" size="sm" onClick={addItem} className="shrink-0">
                    <Plus className="h-4 w-4 mr-1" />
                    Position hinzufügen
                  </Button>
                </div>

                {/* Header Row */}
                <div className="grid grid-cols-12 gap-3 mb-2 px-2">
                  <div className="col-span-3">
                    <span className="text-xs font-medium text-gray-700">Beschreibung *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">Menge *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">Einzelpreis ({formData.country === 'CH' ? 'CHF' : '€'}) *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">USt. (%) *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">Summe</span>
                  </div>
                  <div className="col-span-1">
                    {/* Action column */}
                  </div>
                </div>

                {/* Item Rows */}
                {formData.items.map((item, index) => (
                  <div key={index} className="bg-white border rounded-xl p-3 mb-3 hover:shadow-sm transition-shadow">
                    {/* Standardartikel-Picker (Feb 2026, Sprint 1) — füllt Felder, alle bleiben editierbar */}
                    {standardArticles.length > 0 && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                          Aus Standardartikel:
                        </label>
                        <select
                          className="flex-1 max-w-xs h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                          value=""
                          onChange={(e) => { applyStandardArticle(index, e.target.value); e.target.value = ''; }}
                          data-testid={`standard-article-picker-${index}`}
                        >
                          <option value="">— Vorlage wählen —</option>
                          {standardArticles.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name} ({a.unit_price.toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'} / {a.default_unit})
                            </option>
                          ))}
                        </select>
                        <span className="text-[11px] text-slate-400 italic">
                          alle Felder bleiben editierbar
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-3">
                        <Input
                          placeholder="z.B. Webdesign, Beratung, Produkt..."
                          value={item.description}
                          onChange={(e) => handleItemChange(index, "description", e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", parseFloat(e.target.value) || 0)}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-2">
                        <CurrencyInput
                          country={formData.country || user?.country || 'DE'}
                          value={item.unit_price === 0 ? null : item.unit_price}
                          onChange={(num) => handleItemChange(index, "unit_price", num ?? 0)}
                          label={null}
                          className="h-10"
                          data-testid={`invoice-item-unit-price-${index}`}
                        />
                      </div>
                      <div className="col-span-2 relative">
                        {(() => {
                          // Feb 2026 — bei KU UND Cross-Border (RC/IGL/Export) ist der USt-Satz per Gesetz 0 %.
                          // Statt eines Dropdowns mit 19/7/0 zeigen wir ein gesperrtes Badge.
                          const sellerCountry = (user?.country || 'DE').toUpperCase();
                          const selectedCustomer = useInlineCustomer
                            ? inlineCustomer
                            : customers.find(c => c.id === formData.customer_id);
                          const customerCountry = (selectedCustomer?.country_code || '').toUpperCase();
                          const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
                          const scenario = formData.tax_scenario || (isCrossBorder ? detectTaxScenario() : 'standard');
                          const isLockedZero = !isKU && isCrossBorder && scenario && scenario !== 'standard';

                          if (isKU) {
                            return (
                              <div className="h-10 flex items-center justify-center text-xs text-amber-600 font-medium bg-amber-50 rounded-md border border-amber-200">
                                0 % (KU)
                              </div>
                            );
                          }
                          if (isLockedZero) {
                            const scenarioShort = scenario === 'reverse_charge' ? 'RC'
                              : scenario === 'intra_community' ? 'IGL'
                              : 'Export';
                            return (
                              <div
                                className="h-10 flex items-center justify-center text-xs text-blue-700 font-medium bg-blue-50 rounded-md border border-blue-200"
                                data-testid={`invoice-item-tax-rate-${index}`}
                                title={`Bei ${scenarioShort} ist der USt-Satz per Gesetz 0 % — kein USt-Ausweis möglich.`}
                              >
                                0 % ({scenarioShort})
                              </div>
                            );
                          }
                          return (
                            <Select
                              value={item.tax_rate.toString()}
                              onValueChange={(value) => handleItemChange(index, "tax_rate", parseFloat(value))}
                            >
                              <SelectTrigger className="h-10 w-full" data-testid={`invoice-item-tax-rate-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getVatRates(formData.country).map(rate => (
                                  <SelectItem key={rate.value} value={rate.value.toString()}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{rate.label}</span>
                                      <span className="text-xs text-gray-500">{rate.description}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </div>
                      <div className="col-span-2">
                        <div className="h-10 flex items-center justify-end pr-2">
                          <span className="font-semibold text-gray-900">
                            {item.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          className="hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                {isKU && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded px-3 py-2 mb-2">
                    Kleinunternehmerregelung aktiv – keine USt. wird ausgewiesen.
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">{t("invoices.subtotal")}:</span>
                  <span>{calculateSubtotal().toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                </div>
                {!isKU && (
                  <div className="flex justify-between">
                    <span className="font-medium">{t("invoices.taxTotal")}:</span>
                    <span>{calculateTaxTotal().toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>{t("invoices.grandTotal")}:</span>
                  <span>{(calculateSubtotal() + calculateTaxTotal()).toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                </div>
              </div>

              {/* Feb 2026 — Compliance-Preview: Pflichthinweis bei KU 1:1 wie auf PDF.
                  Cross-Border-Hinweis ist bereits oben in der Steuerart-Card integriert
                  (siehe `invoice-tax-scheme-legal-note`). Hier wird der KU-Fall ergänzt,
                  damit der FL den genauen Text vorab sieht. */}
              {isKU && (
                <LegalStatementPreview
                  country={user?.kleinunternehmer_country || user?.country || formData.country || 'DE'}
                  scenario="small_business"
                  isKleinunternehmer={true}
                  variant="card"
                />
              )}

              <div className="bg-gray-50 border rounded-xl p-4">
                <Label className="text-sm font-semibold">💬 {t("invoices.notes")} (optional)</Label>
                <p className="text-xs text-gray-600 mb-2">
                  Zusätzliche Hinweise für den Kunden (z.B. Zahlungsbedingungen, Danksagung)
                </p>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder={t('invoices.notesExample', 'e.g. Thank you for your order! Payment by bank transfer please.')}
                />
              </div>

              <InvoiceDesignOverride
                value={formData.design_override}
                locked={!!editingInvoice && !["draft", "created"].includes(editingInvoice.status || "created")}
                defaultDesign={userSettings?.invoice_design?.design}
                defaultAccent={userSettings?.invoice_design?.accent_color}
                onChange={(v) => setFormData({ ...formData, design_override: v })}
              />

              {/* EPC QR Code Option (Pro only) */}
              {(user?.has_lifetime_access || ['pro', 'pro_monthly', 'pro_yearly', 'lifetime'].includes((user?.plan || '').toLowerCase())) &&
               formData.country !== 'CH' && (
                <div className="bg-gray-50 border rounded-xl p-4">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="payment-qr-toggle">
                    <input
                      type="checkbox"
                      checked={formData.include_payment_qr || false}
                      onChange={(e) => setFormData({ ...formData, include_payment_qr: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      data-testid="payment-qr-checkbox"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-gray-900">Zahlungs-QR-Code</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium rounded">PRO</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        EPC-QR-Code (GiroCode) auf der Rechnung anzeigen. Dein Kunde kann den Code mit seiner Banking-App scannen, um die Überweisung direkt auszuführen.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    const currentCountry = formData.country;
                    setFormData({
                      customer_id: "",
                      project_id: "",
                      invoice_date: format(new Date(), "yyyy-MM-dd"),
                      due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
                      service_date: "",
                      service_date_end: "",
                      country: currentCountry,
                      status: "created",
                      notes: "",
                      buyer_reference: "",
                      items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
                    });
                  }}
                  className="shrink-0"
                  title={t('invoices.resetForm', 'Reset form')}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                
                {/* Preview Button - Only show for new invoices */}
                {!editingInvoice && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handlePreview}
                    disabled={isLoading}
                    className="flex items-center gap-2"
                    data-testid="preview-invoice-button"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Wird generiert...
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Vorschau
                      </>
                    )}
                  </Button>
                )}
                
                <Button type="submit" className="w-full" data-testid="save-invoice-button">
                  {editingInvoice ? t("common.save") : t("common.create")}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>

          {/* Tax Help Modal */}
          <Dialog open={taxHelpModalOpen} onOpenChange={setTaxHelpModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HelpCircle className="h-6 w-6 text-blue-600" />
                  Hilfe: Welcher Steuersatz gilt?
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 text-sm">
                {formData.country === 'DE' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇩🇪 Deutschland - Umsatzsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">19% (Regelsteuersatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">7% (Ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel (außer Luxusartikel)</li>
                            <li>Bücher, Zeitungen, Zeitschriften</li>
                            <li>Personennahverkehr (ÖPNV)</li>
                            <li>Kulturelle Leistungen (Theater, Museen)</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Reverse Charge / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li><strong>Reverse Charge:</strong> B2B-Geschäfte ins EU-Ausland (Kunde hat UID-Nummer)</li>
                            <li><strong>Kleinunternehmerregelung:</strong> Umsatz {'<'} 22.000 € (Vorjahr) {'&'} {'<'} 50.000 € (laufendes Jahr)</li>
                          </ul>
                          <p className="text-sm text-orange-700 mt-2">
                            ⚠️ Hinweis: Bei Reverse Charge wird automatisch „Steuerschuldnerschaft des Leistungsempfängers gemäß § 13b UStG." auf der Rechnung ausgewiesen — du musst nichts manuell eintragen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {formData.country === 'AT' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇦🇹 Österreich - Umsatzsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">20% (Normalsteuersatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">13% (Ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel</li>
                            <li>Bücher, Zeitungen</li>
                            <li>Kulturelle Dienstleistungen</li>
                            <li>Wohnungsvermietung (bestimmte Fälle)</li>
                          </ul>
                        </div>
                        
                        <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                          <p className="font-semibold mb-1">10% (Stark ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Bücher (Verlagserzeugnisse)</li>
                            <li>Bestimmte Lebensmittel</li>
                            <li>Medikamente</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Reverse Charge / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li><strong>Reverse Charge:</strong> B2B-Geschäfte ins EU-Ausland (Kunde hat UID-Nummer)</li>
                            <li><strong>Kleinunternehmerregelung:</strong> Jahresumsatz {'<'} 35.000 €</li>
                          </ul>
                          <p className="text-sm text-orange-700 mt-2">
                            ⚠️ Hinweis: Bei Reverse Charge wird automatisch „Steuerschuldnerschaft des Leistungsempfängers gemäß § 19 Abs. 1a UStG 1994." auf der Rechnung ausgewiesen — du musst nichts manuell eintragen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {formData.country === 'CH' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇨🇭 Schweiz - Mehrwertsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">8,1% (Normalsatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">3,8% (Sondersatz Beherbergung)</p>
                          <p className="text-gray-700">Gilt für Hotelleistungen und Beherbergung</p>
                        </div>
                        
                        <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                          <p className="font-semibold mb-1">2,6% (Reduzierter Satz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel (außer Getränke)</li>
                            <li>Bücher, Zeitungen, Zeitschriften</li>
                            <li>Medikamente</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Steuerfrei / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Export von Leistungen ins Ausland</li>
                            <li>Kleinunternehmer (Jahresumsatz {'<'} CHF 100'000)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="bg-gray-50 border border-gray-300 p-4 rounded">
                  <p className="font-semibold mb-2">💡 Wichtiger Hinweis:</p>
                  <p className="text-gray-700">
                    Diese Angaben dienen nur zur Orientierung und ersetzen keine professionelle Steuerberatung. 
                    Im Zweifelsfall konsultieren Sie bitte Ihren Steuerberater.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={() => setTaxHelpModalOpen(false)}>
                  Verstanden
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      </div>


      {/* Statistics Cards — einheitliche KPI-Karten (Styleguide Juni 2026) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Gesamt"
          value={calculateStats().count}
          subtitle={`${calculateStats().total.toFixed(2)} €`}
          icon={FileText}
          iconColor="bg-slate-100 text-slate-500"
          testId="invoice-kpi-total"
        />
        <KPICard
          title="Entwurf"
          value={filteredInvoices.filter(i => i.status === "created" || i.status === "draft").length}
          subtitle={`${calculateStats().created.toFixed(2)} €`}
          icon={FileText}
          iconColor="bg-slate-100 text-slate-500"
          testId="invoice-kpi-draft"
        />
        <KPICard
          title="Offen"
          value={filteredInvoices.filter(i => i.status === "sent").length}
          valueClassName="text-amber-600"
          subtitle={`${calculateStats().open.toFixed(2)} €`}
          icon={TrendingUp}
          iconColor="bg-amber-50 text-amber-600"
          testId="invoice-kpi-open"
        />
        <KPICard
          title="Bezahlt"
          value={filteredInvoices.filter(i => i.status === "paid").length}
          valueClassName="text-emerald-600"
          subtitle={`${calculateStats().paid.toFixed(2)} €`}
          icon={TrendingUp}
          iconColor="bg-emerald-50 text-emerald-600"
          testId="invoice-kpi-paid"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center text-base">
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </CardTitle>
          <button
            onClick={toggleShowLocked}
            className={`shrink-0 h-8 px-2.5 rounded-md border text-xs flex items-center gap-1.5 transition-colors ${
              showLocked ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                         : 'bg-white text-slate-600 border-input hover:bg-zinc-50'
            }`}
            title="GoBD-festgeschriebene Rechnungen ein-/ausblenden"
            data-testid="toggle-show-locked-invoices">
            {showLocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            Festgeschr. {showLocked ? 'an' : 'aus'}
          </button>
        </CardHeader>
        <CardContent>
          {/* Schnellfilter-Chips — 1-Klick-Zugriff auf häufige Ansichten */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="invoice-quick-filters">
            {(() => {
              const counts = getQuickFilterCounts();
              return quickFilters.map(qf => {
                const active = statusFilter === qf.key;
                const isOverdue = qf.key === "overdue";
                return (
                  <button
                    key={qf.key}
                    onClick={() => setStatusFilter(qf.key)}
                    data-testid={`quick-filter-${qf.key}`}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                      active
                        ? (isOverdue ? "bg-rose-600 text-white border-rose-600" : "bg-slate-900 text-white border-slate-900")
                        : (isOverdue && counts[qf.key] > 0
                            ? "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
                            : "bg-white text-slate-600 border-input hover:bg-zinc-50")
                    }`}
                  >
                    {qf.label}
                    <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[11px] ${
                      active ? "bg-white/20" : "bg-slate-100 text-slate-600"
                    }`}>
                      {counts[qf.key]}
                    </span>
                  </button>
                );
              });
            })()}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Monat</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="draft">Entwurf</SelectItem>
                  <SelectItem value="sent">Versendet (Offen)</SelectItem>
                  <SelectItem value="overdue">Überfällig</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        {listLoading && invoices.length === 0 && (
          <div className="space-y-4" data-testid="invoices-loading-skeleton">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="h-5 w-40 rounded bg-slate-200" />
                      <div className="h-3 w-28 rounded bg-slate-100" />
                    </div>
                    <div className="h-6 w-20 rounded-full bg-slate-200" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="h-4 w-48 rounded bg-slate-100" />
                    <div className="h-8 w-28 rounded bg-slate-200" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {invoices.length > filteredInvoices.length && !showLocked && invoices.some(i => i.is_locked) && (
          <div className="text-sm text-slate-500 flex items-center gap-1.5 px-1" data-testid="fl-invoices-locked-hint">
            <Lock className="w-3.5 h-3.5" />
            {invoices.filter(i => i.is_locked).length} Rechnung(en) in festgeschriebenen Perioden ausgeblendet — klicke „Festgeschrieben" im Filter, um sie anzuzeigen.
          </div>
        )}
        {filteredInvoices.slice(0, visibleCount).map((invoice, index) => (
          <Card key={invoice.id} className={`card-hover ${invoice.is_locked ? 'opacity-70' : ''}`} data-testid={`invoice-card-${invoice.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start space-x-3 min-w-0 flex-1">
                  <div className="p-2 bg-emerald-50 rounded-xl">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex flex-wrap items-center gap-2">
                      {invoice.is_locked && <Lock className="w-4 h-4 text-slate-500" title={`Festgeschrieben: ${invoice.locked_period_label || ''}`} data-testid={`fl-inv-lock-${invoice.id}`} />}
                      {invoice.invoice_number}
                      {/* Feb 2026 — Typ-Badge: Storno / Korrektur */}
                      {invoice.invoice_type === "storno" && (
                        <Badge variant="destructive" className="bg-rose-100 text-rose-800 border-rose-200" data-testid={`badge-storno-${invoice.id}`}>
                          <FileWarning className="w-3 h-3 mr-1" /> Storno
                        </Badge>
                      )}
                      {invoice.invoice_type === "correction" && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`badge-correction-${invoice.id}`}>
                          <FilePen className="w-3 h-3 mr-1" /> Korrektur
                        </Badge>
                      )}
                      {invoice.is_recurring_occurrence && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer" data-testid={`badge-recurring-${invoice.id}`} onClick={() => navigate("/recurring")}>
                          Aus Dauerauftrag
                        </Badge>
                      )}
                      {/* Juni 2026 — ZUGFeRD-Häkchen: PDF ist bereits gültige E-Rechnung (DE/AT, EUR) */}
                      {["DE", "AT"].includes(invoice.country) && (invoice.currency || "EUR") === "EUR" && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`badge-einvoice-${invoice.id}`}
                          title="Die PDF enthält das eingebettete ZUGFeRD-XML (EN 16931) — gültige E-Rechnung, Pflicht 2027/2028 ready">
                          <BadgeCheck className="w-3 h-3 mr-1" /> E-Rechnung
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{invoice.customer_name}</p>
                    {invoice.project_name && (
                      <p className="text-xs text-gray-500">{invoice.project_name}</p>
                    )}
                    {/* Storno-/Korrektur-Verweise */}
                    {invoice.cancels_invoice_id && (
                      <p className="text-xs text-rose-700 mt-1">storniert Original-Rechnung</p>
                    )}
                    {invoice.cancelled_by_invoice_id && (
                      <p className="text-xs text-rose-700 mt-1">durch Stornorechnung aufgehoben</p>
                    )}
                    {invoice.correction_replaces_invoice_id && (
                      <p className="text-xs text-amber-700 mt-1">ersetzt eine frühere Rechnung (Korrektur)</p>
                    )}
                    {invoice.correction_reason && (invoice.invoice_type === "storno" || invoice.invoice_type === "correction") && (
                      <p className="text-xs text-slate-500 mt-1 italic">Grund: {invoice.correction_reason}</p>
                    )}
                    {getDueDateStatus(invoice.due_date, invoice.status)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {getStatusBadge(invoice.status)}
                  {sbCommentInvoiceIds.has(invoice.id) && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 font-medium" data-testid={`sb-comment-indicator-${invoice.id}`}>
                      <MessageSquare className="w-3 h-3" />SB
                    </span>
                  )}
                  {invoice.last_modified_by_advisor && (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-md hover:bg-slate-100 transition-colors cursor-help" data-testid="sb-invoice-indicator">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-slate-900 text-white text-xs px-2.5 py-1.5 rounded shadow-lg border-0">
                          <p>Status vom SB geändert{invoice.last_modified_by_advisor_name ? ` · ${invoice.last_modified_by_advisor_name}` : ''}{invoice.last_modified_by_advisor_at ? ` · ${new Date(invoice.last_modified_by_advisor_at).toLocaleDateString('de-DE')}` : ''}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.invoiceDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.invoice_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.dueDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.due_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.subtotal")}</p>
                  <p className="font-medium">{invoice.subtotal?.toFixed(2)} {invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{invoice.country === 'CH' ? 'MwSt.' : 'USt.'}</p>
                  <p className="font-medium">{(invoice.tax_total || 0).toFixed(2)} {invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.grandTotal")}</p>
                  <p className="font-semibold text-lg text-emerald-600">{invoice.total?.toFixed(2)} {invoice.currency}</p>
                </div>
              </div>

              {/* Editable paid date — only shown when invoice is paid */}
              {invoice.status === 'paid' && (
                <PaidAtEditor invoice={invoice} onSave={updateInvoicePaidAt} />
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadPDF(invoice.id)}
                  data-testid={`download-pdf-${invoice.id}`}
                >
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                
                {/* XRechnung Export Button - Only for German invoices */}
                {invoice.country === 'DE' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadXRechnung(invoice.id)}
                    data-testid={`download-xrechnung-${invoice.id}`}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <FileX className="h-4 w-4 mr-1" />
                    XRechnung
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendEmail(invoice)}
                  data-testid={`send-email-${invoice.id}`}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Per E-Mail senden
                </Button>
                
                {/* Feb 2026 — Edit-Button NUR im Entwurf, NIE bei sent/paid/cancelled (GoBD) */}
                {(invoice.status === "created" || invoice.status === "draft") && invoice.email_status !== "sent" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditInvoice(invoice)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid={`edit-invoice-${invoice.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Bearbeiten
                  </Button>
                )}

                {/* Status update buttons */}
                {(invoice.status === "created" || invoice.status === "draft") && invoice.email_status !== "sent" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "sent")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Als versendet markieren
                  </Button>
                )}
                {invoice.status === "sent" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "paid")}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    Als bezahlt markieren
                  </Button>
                )}

                {/* Feb 2026 — P0: Stornorechnung + Korrekturrechnung für versendete/bezahlte Rechnungen.
                    NUR für reguläre Rechnungen (nicht für Stornos und nicht für bereits stornierte). */}
                {(invoice.status === "sent" || invoice.status === "paid" || invoice.status === "overdue") &&
                 (invoice.invoice_type || "invoice") === "invoice" &&
                 !invoice.cancelled_by_invoice_id && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setStornoKorrekturInvoice(invoice);
                        setStornoKorrekturMode("storno");
                        setStornoKorrekturOpen(true);
                      }}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`storno-invoice-${invoice.id}`}
                    >
                      <FileWarning className="h-4 w-4 mr-1" />
                      Stornieren
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setStornoKorrekturInvoice(invoice);
                        setStornoKorrekturMode("korrektur");
                        setStornoKorrekturOpen(true);
                      }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`korrektur-invoice-${invoice.id}`}
                    >
                      <FilePen className="h-4 w-4 mr-1" />
                      Korrigieren
                    </Button>
                  </>
                )}

                {/* Juni 2026 — Mahnwesen: nur für offene (nicht bezahlte) versendete/überfällige Rechnungen */}
                {(invoice.status === "sent" || invoice.status === "overdue") &&
                 (invoice.invoice_type || "invoice") === "invoice" &&
                 !invoice.paid_at &&
                 !invoice.cancelled_by_invoice_id &&
                 invoice.due_date &&
                 (() => {
                   const overdueDays = Math.floor((Date.now() - new Date(invoice.due_date).getTime()) / 86400000);
                   return overdueDays >= 1;
                 })() && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDunningInvoice(invoice);
                      setDunningOpen(true);
                    }}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid={`dunning-invoice-${invoice.id}`}
                  >
                    <Bell className="h-4 w-4 mr-1" />
                    {invoice.dunning_level > 0 ? `Mahnen (Stufe ${invoice.dunning_level})` : "Mahnen"}
                  </Button>
                )}

                {/* Reset-to-draft (P0 Feb 2026: nur sichtbar wenn KEINE der Schutz-Bedingungen greift —
                    also wenn weder Storno-relevanter Status noch eine Verknüpfung besteht).
                    Nach GoBD eigentlich unzulässig, bleibt aber als Quick-Fix erhalten solange keine
                    Verknüpfungen existieren. */}
                {invoice.status !== "created" && invoice.status !== "draft" && invoice.status !== "cancelled" &&
                 !invoice.cancelled_by_invoice_id &&
                 (invoice.invoice_type || "invoice") === "invoice" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResetClick(invoice.id)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    title={t('invoices.resetToDraft', 'Reset to draft')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}

                {/* Delete-Button — nur für Entwürfe sichtbar (P0 Delete-Schutz Feb 2026) */}
                {(invoice.status === "created" || invoice.status === "draft") &&
                 (invoice.invoice_type || "invoice") === "invoice" &&
                 !invoice.cancels_invoice_id &&
                 !invoice.cancelled_by_invoice_id &&
                 !invoice.correction_replaces_invoice_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteClick(invoice.id)}
                    data-testid={`delete-invoice-${invoice.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                )}
              </div>
              {/* SB Comments — nur rendern wenn die Rechnung tatsächlich Anmerkungen hat
                  (verhindert 1 API-Call pro Karte = N+1-Problem beim Listen-Render) */}
              {sbCommentInvoiceIds.has(invoice.id) && (
                <InvoiceAdvisorComments invoiceId={invoice.id} />
              )}
            </CardContent>
          </Card>
        ))}
        {!listLoading && filteredInvoices.length > visibleCount && (
          <div className="flex justify-center pt-2" data-testid="invoices-load-more-wrap">
            <Button
              variant="outline"
              onClick={() => setVisibleCount((c) => c + 20)}
              data-testid="invoices-load-more-btn"
            >
              Weitere {Math.min(20, filteredInvoices.length - visibleCount)} von {filteredInvoices.length - visibleCount} laden
            </Button>
          </div>
        )}
      </div>

      {!listLoading && filteredInvoices.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {t("invoices.noInvoices", "Noch keine Rechnungen vorhanden. Erstellen Sie Ihre erste Rechnung!")}
          </CardContent>
        </Card>
      )}
      

    <UpgradeModal
      isOpen={upgradeModalOpen}
      onClose={() => setUpgradeModalOpen(false)}
      message={upgradeMessage}
    />

    <InvoiceEmailModal
      isOpen={emailModalOpen}
      onClose={handleEmailModalClose}
      invoice={selectedInvoiceForEmail}
      companyName={userSettings?.company_name}
      onEmailSent={() => {
        // Sofortiges optimistisches Status-Update (created/draft → sent), dann Server-Sync
        const sentId = selectedInvoiceForEmail?.id;
        if (sentId) {
          setInvoices(list => list.map(inv =>
            inv.id === sentId && (inv.status === "created" || inv.status === "draft")
              ? { ...inv, status: "sent", sent_at: new Date().toISOString() }
              : inv
          ));
        }
        fetchInvoices();
      }}
    />

    {/* Feb 2026 — Storno + Korrektur Modal (P0)
        `key` erzwingt Remount bei Wechsel von Invoice/Mode → Initial-State sauber ohne useEffect. */}
    <StornoKorrekturModal
      key={`${stornoKorrekturInvoice?.id || 'none'}-${stornoKorrekturMode}`}
      open={stornoKorrekturOpen}
      mode={stornoKorrekturMode}
      invoice={stornoKorrekturInvoice}
      onClose={() => setStornoKorrekturOpen(false)}
      onSuccess={() => {
        // Reload invoices list after storno/korrektur
        fetchInvoices?.();
      }}
    />

    <DunningModal
      key={`dunning-${dunningInvoice?.id || 'none'}-${dunningInvoice?.dunning_level || 0}`}
      open={dunningOpen}
      invoice={dunningInvoice}
      onClose={() => setDunningOpen(false)}
      onSent={() => fetchInvoices?.()}
    />

    {/* QR Fallback Modal */}
    <Dialog open={qrFallbackModalOpen} onOpenChange={setQrFallbackModalOpen}>
      <DialogContent className="w-[min(90vw,900px)] max-w-none mx-4 max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
            <span className="break-words">{t('invoices.qrFallback.title')}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 break-words">
            {t('invoices.qrFallback.description')}
          </p>
          
          {qrFallbackData?.qr_validation?.validation_errors && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 overflow-hidden max-w-full">
              <ul className="space-y-2 text-sm">
                {qrFallbackData.qr_validation.validation_errors.map((error, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5 flex-shrink-0">•</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-orange-800 break-words">{error.message}</div>
                      {error.recommendation && (
                        <div className="text-orange-600 text-xs mt-1 break-words">
                          → {error.recommendation}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <p className="text-sm text-gray-600 break-words">
            {t('invoices.qrFallback.fallbackQuestion')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={handleQrFallbackCancel} className="w-full sm:w-auto">
              {t('invoices.qrFallback.cancel')}
            </Button>
            <Button variant="outline" onClick={handleOpenSettings} className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{t('invoices.qrFallback.openSettings')}</span>
            </Button>
            <Button onClick={handleQrFallbackConfirm} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto">
              <span className="truncate">{t('invoices.qrFallback.createStandard')}</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog({ isOpen: false, type: null, invoiceId: null, title: '', description: '' })}
      onConfirm={handleConfirmAction}
      title={confirmDialog.title}
      description={confirmDialog.description}
      confirmText={confirmDialog.type === 'delete' ? t('common.delete', 'Löschen') : t('common.confirm', 'Bestätigen')}
      cancelText={t('common.cancel', 'Abbrechen')}
      variant={confirmDialog.type === 'delete' ? 'destructive' : 'default'}
    />

    {/* Invoice Preview Modal */}
    <InvoicePreviewModal
      isOpen={previewModalOpen}
      onClose={() => setPreviewModalOpen(false)}
      onEdit={handlePreviewEdit}
      onCreate={handlePreviewCreate}
      previewPdfUrl={previewPdfUrl}
      isLoading={isLoading}
    />

    {/* Konto-sichern-Moment: nach erster eigener Rechnung eines Gasts */}
    <GuestClaimModal
      open={guestClaimOpen}
      onOpenChange={setGuestClaimOpen}
      title="Geschafft — deine erste Rechnung!"
      intro="Stark! Sichere sie jetzt in einem Konto, damit sie (samt Kunden & Projekten) dauerhaft erhalten bleibt. Nur E-Mail und Passwort nötig."
    />

    {/* Entwurf-Schutz (2026): Rückfrage beim Schließen mit ungespeicherten Eingaben */}
    <Dialog open={draftGuardOpen} onOpenChange={setDraftGuardOpen}>
      <DialogContent className="max-w-lg" data-testid="invoice-draft-guard-dialog">
        <DialogHeader>
          <DialogTitle>Rechnung als Entwurf speichern?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-700">
            Du hast begonnene, noch nicht gespeicherte Eingaben. Möchtest du diese Rechnung als
            Entwurf speichern? Sonst gehen deine Eingaben verloren.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:justify-end pt-2">
          <Button
            variant="ghost"
            onClick={() => setDraftGuardOpen(false)}
            className="whitespace-nowrap"
            data-testid="draft-guard-back-btn"
          >
            Zurück
          </Button>
          <Button
            variant="outline"
            onClick={discardInvoiceDraft}
            className="text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
            data-testid="draft-guard-discard-btn"
          >
            Verwerfen
          </Button>
          <Button
            onClick={saveInvoiceAsDraft}
            className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
            data-testid="draft-guard-save-btn"
          >
            Als Entwurf speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Feb 2026 — Inline-Kunde "in Kunden-DB übernehmen?"-Dialog */}
    <Dialog
      open={promoteDialog.isOpen}
      onOpenChange={(open) => { if (!open) setPromoteDialog({ isOpen: false, customerId: null, customerName: "" }); }}
    >
      <DialogContent className="max-w-md" data-testid="promote-customer-dialog">
        <DialogHeader>
          <DialogTitle>Kunde in deine Kunden-DB übernehmen?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-700">
            Du hast „<span className="font-semibold">{promoteDialog.customerName}</span>" gerade direkt in der
            Rechnung erfasst. Sollen die Kundendaten dauerhaft in deine Kundenliste übernommen werden?
          </p>
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
            <strong>Tipp:</strong> Bei Übernahme kannst du den Kunden später für Folgerechnungen, Projekte oder Mahnungen wiederverwenden.
            Andernfalls bleibt der Kunde nur an dieser einen Rechnung verknüpft.
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setPromoteDialog({ isOpen: false, customerId: null, customerName: "" })}
            data-testid="promote-customer-skip"
          >
            Nein, nur diese Rechnung
          </Button>
          <Button
            type="button"
            onClick={async () => {
              try {
                await api.post(`/customers/${promoteDialog.customerId}/promote`);
                toast.success(`„${promoteDialog.customerName}" zur Kunden-DB hinzugefügt`);
                fetchCustomers();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Konnte Kunde nicht übernehmen.");
              } finally {
                setPromoteDialog({ isOpen: false, customerId: null, customerName: "" });
              }
            }}
            data-testid="promote-customer-confirm"
          >
            Ja, übernehmen
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    </div>
  );
}

// ─── Advisor Comments per Invoice ───
function InvoiceAdvisorComments({ invoiceId }) {
  const [comments, setComments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (!invoiceId || loaded) return;
    api.get(`/advisor/comments/for-invoice/${invoiceId}`)
      .then(r => setComments(r.data.comments || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [invoiceId, loaded]);

  const respond = async (commentId, action) => {
    try {
      await api.post(`/advisor/comments/${commentId}/reply`, { action });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, response_status: action } : c));
    } catch {}
  };

  const sendReply = async (commentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/advisor/comments/${commentId}/reply`, { text: replyText.trim() });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, reply_text: replyText.trim() } : c));
      setReplyingTo(null);
      setReplyText('');
    } catch {}
  };

  const resolveComment = async (commentId) => {
    try {
      await api.post(`/advisor/comments/${commentId}/resolve`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {}
  };

  if (!loaded || comments.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-blue-200" data-testid={`invoice-comments-${invoiceId}`}>
      <div className="text-xs font-semibold text-blue-600 uppercase tracking-[0.05em] mb-2 flex items-center gap-1.5">
        <MessageSquare className="w-3.5 h-3.5" />
        SB-Anmerkungen ({comments.length})
      </div>
      <div className="space-y-2">
        {comments.map(c => (
          <div key={c.id} className="p-2.5 bg-blue-50/50 border border-blue-100 rounded text-sm" data-testid={`inv-comment-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <span className="text-[11px] text-slate-500">{c.advisor_name || 'SB'} · {new Date(c.created_at).toLocaleDateString('de-DE')}</span>
                <p className="text-slate-800 mt-0.5">{c.text}</p>
                {c.reply_text && (
                  <div className="mt-1.5 pl-2.5 border-l-2 border-emerald-300">
                    <p className="text-xs text-emerald-700">Ihre Antwort: {c.reply_text}</p>
                  </div>
                )}
              </div>
              {c.response_status ? (
                <div className="flex gap-1 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${c.response_status === 'confirm' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                    {c.response_status === 'confirm' ? 'OK' : 'Nein'}
                  </span>
                  <button onClick={() => resolveComment(c.id)} className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200">Erledigt</button>
                </div>
              ) : c.reply_text ? (
                <button onClick={() => resolveComment(c.id)} className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 shrink-0">Erledigt</button>
              ) : (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => respond(c.id, 'confirm')} className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100">OK</button>
                  <button onClick={() => respond(c.id, 'reject')} className="text-[10px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200 hover:bg-red-100">Nein</button>
                  <button onClick={() => { setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText(''); }}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${replyingTo === c.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'}`}>
                    Antworten
                  </button>
                </div>
              )}
            </div>
            {replyingTo === c.id && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Ihre Antwort..."
                  className="flex-1 text-xs border border-slate-300 rounded px-2 py-1.5"
                  onKeyDown={e => e.key === 'Enter' && sendReply(c.id)}
                />
                <button onClick={() => sendReply(c.id)} disabled={!replyText.trim()}
                  className="text-[10px] px-2 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                  Senden
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PaidAtEditor — inline editable payment date for paid invoices ───
function PaidAtEditor({ invoice, onSave }) {
  const initialDate = invoice.paid_at ? String(invoice.paid_at).slice(0, 10) : '';
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(initialDate);

  // Re-sync when invoice updates externally
  useEffect(() => { setDate(initialDate); }, [initialDate]);

  const handleSave = async () => {
    if (!date) return;
    if (date === initialDate) { setEditing(false); return; }
    await onSave(invoice.id, date);
    setEditing(false);
  };

  return (
    <div className="mb-3 inline-flex items-center gap-2 text-sm bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-1.5"
         data-testid={`paid-at-${invoice.id}`}>
      <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Bezahlt am</span>
      {editing ? (
        <>
          <Input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="h-7 text-sm w-36"
            data-testid={`paid-at-input-${invoice.id}`}
          />
          <Button size="sm" onClick={handleSave}
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`paid-at-save-${invoice.id}`}>
            Speichern
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDate(initialDate); setEditing(false); }}
            className="h-7 text-xs"
            data-testid={`paid-at-cancel-${invoice.id}`}>
            Abbrechen
          </Button>
        </>
      ) : (
        <>
          <span className="font-mono text-emerald-800 text-sm" data-testid={`paid-at-display-${invoice.id}`}>
            {initialDate ? new Date(initialDate).toLocaleDateString('de-DE') : '—'}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="text-emerald-700 hover:text-emerald-900 p-0.5"
            title="Bezahldatum ändern"
            data-testid={`paid-at-edit-${invoice.id}`}>
            <Edit className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}